import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import html2canvas from 'html2canvas';
import './App.css';
import RoleBadge from './components/RoleBadge';
import chatData from './data/chat.json';
import goalsData from './data/goals.json';
import defaultProfile from './personas/default_profile.json';
import ahmedSmall from './personas/ahmed_banking_profile_small.json';
import sarahChen from './personas/sarah_chen_banking_profile_small.json';
import jasonJones from './personas/jason_jones_banking_profile_small.json';
import samOkafor from './personas/sam_okafor_profile_small.json';
import margaretWhitfield from './personas/margaret_whitfield_banking_profile_small.json';
import priyaPatel from './personas/priya_patel_banking_profile_small.json';
import marcusHill from './personas/marcus_hill_banking_profile_small.json';
import justinLee from './personas/justin_lee_banking_profile_small.json';
import {
  MdSwapHoriz, MdCameraAlt, MdReceipt,
  MdAccountBalanceWallet, MdTrendingUp, MdOutlineShoppingCart, MdLocationOn,
  MdChat, MdHome, MdDashboard, MdPerson,
  MdSettings, MdGridView, MdBarChart, MdClose, MdKeyboardArrowDown,
  MdVolumeUp, MdVolumeOff, MdSaveAlt, MdRestartAlt,
  MdRefresh, MdDeleteSweep, MdTerminal, MdSave, MdUndo, MdUpload,
  MdScience, MdPlayArrow, MdRecordVoiceOver,
} from 'react-icons/md';
import { FaRegCreditCard } from 'react-icons/fa';
import { themes } from './constants/themes';
import { computeBudgetRule } from './utils/computeBudgetRule';
import { ELEVENLABS_VOICES, OPENAI_VOICES, ELEVENLABS_DEFAULT_VOICE, OPENAI_DEFAULT_VOICE } from './constants/voices';
import { DEFAULT_PROMPTS, loadSavedPrompts, savePromptsToStorage, clearSavedPrompts } from './constants/systemPrompt';
import { SiElevenlabs, SiOpenai } from 'react-icons/si';
import { ProfileContext } from './contexts/ProfileContext';
import { AppContext } from './contexts/AppContext';
import { loadProfile as loadProfileToServer, clearCustomerMemory, getMemory, setAuthToken, authHeaders, changePassword, requestPasswordReset } from './services/backendAPI';
import {
  HomeScreen, AccountsScreen, AccountDetailsScreen, CreditScreen, ChatScreen,
  DirectDepositDetailScreen, ProfileScreen, LocationsScreen, ReceiptScreen,
  CardManagementScreen, SecurityScreen, InAppCallScreen, CallVerificationScreen,
  SpendingAnalysisScreen, SpendingScreen, InsightsScreen, GoalsScreen, AgentsScreen, AdminScreen, FinancesScreen,
  DashboardScreen, FinancialSummaryScreen, BudgetRuleScreen, BudgetScreen, ScenarioScreen, ChatHistoryScreen,
  VoiceBotScreen, EmailMoneyTransferScreen, BillPaymentScreen, ServicesHubScreen,
  AccountTransferScreen, MobileDepositScreen, CrossBorderScreen, AutoServicesScreen, PreAuthorizedScreen,
  PortfolioScreen, RegisteredAccountsScreen,
  TodaysRatesScreen, MortgagesScreen, OpenAccountsScreen,
  NotificationsScreen, CardsScreen, SubscriptionsScreen,
  SettingsScreen, HelpScreen, TravelNoticeScreen, StatementsScreen, RewardsScreen, PayeesScreen,
  MortgageDetailScreen,
  TestScenariosScreen, hasGroundTruth,
  ChangePasswordScreen,
  ResetRequestForm,
  SearchScreen,
  TransactionDetailScreen,
  InvestingScreen,
  AdvisorScreen,
  BookAppointmentScreen,
  FlightSearchScreen,
  SwipeScreen,
  UpcomingFeaturesScreen,
  DemoStateScreen,
} from './screens';
import ScenarioConsoleTab from './components/ScenarioConsoleTab';

// --- MOCK DATA ---
const initialChatHistory = chatData.initialChatHistory;

// --- MODEL SELECTION ---
// Default model to select when a provider is chosen (used until/unless the live
// list from /api/models is fetched and the default isn't offered there).
const PROVIDER_DEFAULT_MODEL = {
  cohere:     'command-r-08-2024',
  openrouter: 'nvidia/nemotron-nano-9b-v2:free',
  claude:     'claude-sonnet-4-6',
  openai:     'gpt-4o-mini',
  ollama:     'qwen3.8:27b-q8_0',
  omlx:       'Qwen3.5-27B-Claude-4.6-Opus-Distilled-MLX-4bit',
  gemini:     'gemini-2.5-flash-lite',
  groq:       'llama-3.3-70b-versatile',
};

const BACKEND_LABEL = {
  cohere: 'Cohere', openrouter: 'OpenRouter', claude: 'Claude', openai: 'OpenAI',
  ollama: 'Ollama', omlx: 'oMLX', gemini: 'Gemini', groq: 'Groq',
};

// Static fallback lists — shown only when the live /api/models fetch fails
// (provider unreachable or no API key configured).
const MODEL_FALLBACKS = {
  cohere: [
    { value: 'command-a-03-2025', label: 'Command A 03-2025' },
    { value: 'command-r-plus-08-2024', label: 'Command R+ 08-2024' },
    { value: 'command-r-08-2024', label: 'Command R 08-2024' },
    { value: 'command-r7b-12-2024', label: 'Command R7B 12-2024' },
    { value: 'command-r-plus', label: 'Command R+ (legacy)' },
    { value: 'command-r', label: 'Command R (legacy)' },
  ],
  claude: [
    { value: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6 (default)' },
    { value: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5' },
    { value: 'claude-3-5-sonnet-20241022', label: 'Claude 3.5 Sonnet' },
    { value: 'claude-3-5-haiku-20241022', label: 'Claude 3.5 Haiku' },
    { value: 'claude-3-haiku-20240307', label: 'Claude 3 Haiku (legacy)' },
  ],
  openai: [
    { value: 'gpt-4o-mini', label: 'GPT-4o mini (default)' },
    { value: 'gpt-4o', label: 'GPT-4o' },
    { value: 'gpt-4-turbo', label: 'GPT-4 Turbo' },
    { value: 'o3-mini', label: 'o3-mini (reasoning)' },
    { value: 'o1-mini', label: 'o1-mini (reasoning)' },
    { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo (legacy)' },
  ],
  gemini: [
    { value: 'gemini-2.5-flash-lite', label: 'Gemini 2.5 Flash-Lite (free default)' },
    { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash' },
    { value: 'gemini-3.1-flash-lite', label: 'Gemini 3.1 Flash-Lite' },
    { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro' },
  ],
  groq: [
    { value: 'llama-3.3-70b-versatile', label: 'Llama 3.3 70B Versatile (default)' },
    { value: 'llama-3.1-8b-instant', label: 'Llama 3.1 8B Instant (fastest)' },
    { value: 'llama-4-scout-17b-16e-instruct', label: 'Llama 4 Scout 17B' },
    { value: 'qwen-qwq-32b', label: 'Qwen QwQ 32B (reasoning)' },
    { value: 'deepseek-r1-distill-llama-70b', label: 'DeepSeek R1 Distill 70B' },
    { value: 'mistral-saba-24b', label: 'Mistral Saba 24B' },
  ],
  openrouter: [
    { value: 'nvidia/nemotron-3-super-120b-a12b:free', label: 'Nemotron 3 Super 120B' },
    { value: 'google/gemma-4-31b-it:free', label: 'Gemma 4 31B' },
    { value: 'openai/gpt-oss-120b:free', label: 'GPT OSS 120B' },
    { value: 'nvidia/nemotron-nano-9b-v2:free', label: 'Nemotron Nano 9B' },
    { value: 'meta-llama/llama-3.3-70b-instruct:free', label: 'Llama 3.3 70B' },
    { value: 'qwen/qwen3-coder-480b:free', label: 'Qwen3 Coder 480B' },
  ],
  ollama: [
    { value: 'qwen3.8:27b-q8_0', label: 'Qwen3.8 27B Q8 (default)' },
    { value: 'qwen3.5:14b', label: 'Qwen3.5 14B' },
    { value: 'qwen3.5:7b', label: 'Qwen3.5 7B' },
    { value: 'llama3.1:8b', label: 'Llama 3.1 8B' },
    { value: 'phi4', label: 'Phi-4' },
  ],
  omlx: [
    { value: 'Qwen3.5-27B-Claude-4.6-Opus-Distilled-MLX-4bit', label: 'Qwen3.5 27B (default)' },
    { value: 'Qwen3.5-14B-MLX-4bit', label: 'Qwen3.5 14B' },
    { value: 'Qwen3.5-7B-MLX-4bit', label: 'Qwen3.5 7B' },
    { value: 'Llama-3.1-8B-Instruct-MLX', label: 'Llama 3.1 8B' },
    { value: 'Gemma-2-9b-it-MLX', label: 'Gemma 2 9B' },
  ],
};


// --- BOTTOM NAV ---

const InvestmentsIcon = ({ size = 22 }) => (
  <span style={{ position: 'relative', display: 'inline-flex', width: size, height: size, flexShrink: 0 }}>
    <MdBarChart size={size} style={{ display: 'block' }} />
    <MdTrendingUp size={Math.round(size * 0.72)} style={{ position: 'absolute', right: -4, top: 2, filter: 'drop-shadow(0 0 2px var(--bg-card))' }} />
  </span>
);

const BOTTOM_TABS = [
  { screen: 'home',          Icon: MdHome,             label: 'Home',        tab: 'home'         },
  { screen: 'portfolio',     Icon: InvestmentsIcon,    label: 'Investments', tab: 'investments'  },
  { screen: 'services',      Icon: MdGridView,         label: 'Services',    tab: 'services'     },
  { screen: 'insightsScreen',Icon: MdDashboard,        label: 'Insights',    tab: 'insights'     },
  { screen: 'profileScreen', Icon: MdSettings,          label: 'Settings',    tab: 'settings'     },
];

const SCREEN_TAB_MAP = {
  home: 'home', accounts: 'home', accountDetails: 'home',
  credit: 'home', cardMgmt: 'home', depositDetail: 'home', receipt: 'home',
  portfolio: 'investments', registeredAccounts: 'investments', goalsScreen: 'investments', financialSummary: 'investments', budgetRule: 'investments',
  services: 'services', emailTransfer: 'services', billPayment: 'services',
  accountTransfer: 'services', mobileDeposit: 'services', crossBorder: 'services',
  autoServices: 'services', preAuth: 'services',
  locations: 'services', call: 'services', callVerify: 'services', voiceBot: 'services',
  insightsScreen: 'insights', agents: 'insights', finances: 'insights', spendingAnalysis: 'insights', spendingScreen: 'insights', dashboard: 'insights', budgetScreen: 'insights',
  security: 'settings', profileScreen: 'settings',
};

const BottomNav = ({ currentScreen, onNavigate, busyTab }) => (
  <nav className="bottom-nav" aria-label="Main navigation">
    {BOTTOM_TABS.map(({ screen, Icon, label, tab }) => {
      const isActive = SCREEN_TAB_MAP[currentScreen] === tab;
      const busy = busyTab === tab && !isActive; // pulse only when not already on the tab
      return (
        <button key={screen} className={`nav-item${isActive ? ' active' : ''}`} onClick={() => onNavigate(screen)} aria-label={busy ? `${label} (IVA is analysing…)` : label} aria-current={isActive ? 'page' : undefined}>
          <span style={{ position: 'relative', display: 'inline-flex' }}>
            <Icon size={22} />
            {busy && <span className="nav-busy-dot" title="IVA is analysing your profile…" />}
          </span>
          <span>{label}</span>
        </button>
      );
    })}
  </nav>
);

// --- NEW COMPONENT: SIDEBAR MENU ---

// ── CSV → temp profile ────────────────────────────────────────────────────────
function parseCSVRow(line) {
  const cells = [];
  let cur = '', inQ = false;
  for (const ch of line) {
    if (ch === '"') { inQ = !inQ; }
    else if (ch === ',' && !inQ) { cells.push(cur.trim()); cur = ''; }
    else { cur += ch; }
  }
  cells.push(cur.trim());
  return cells;
}

function guessAccountType(name) {
  const n = name.toLowerCase();
  if (/credit|card|visa|mastercard|amex/.test(n)) return 'credit_card';
  if (/sav|tfsa|hisa|isa/.test(n)) return 'savings';
  if (/invest|rrsp|portfolio|brokerage/.test(n)) return 'investments';
  return 'chequing';
}

function parseCsvToProfile(text, filename) {
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  if (lines.length < 2) throw new Error('CSV must have a header row and at least one data row.');

  const rawHeaders = parseCSVRow(lines[0]);
  const headers = rawHeaders.map(h => h.toLowerCase().replace(/[^a-z0-9]/g, '_'));

  const col = (...names) => {
    for (const n of names) {
      const i = headers.findIndex(h => h === n || h.startsWith(n) || h.includes(n));
      if (i >= 0) return i;
    }
    return -1;
  };

  const dateCol   = col('date', 'transaction_date', 'posted', 'trans_date');
  const descCol   = col('description', 'merchant', 'name', 'payee', 'memo', 'details', 'narrative');
  const amtCol    = col('amount', 'amt');
  const debitCol  = col('debit', 'withdrawal');
  const creditCol = col('credit', 'deposit');
  const catCol    = col('category', 'type', 'transaction_type');
  const acctCol   = col('account', 'account_name', 'account_number');
  const balCol    = col('balance', 'running_balance', 'balance_after');

  if (dateCol < 0) throw new Error('No Date column found. Expected: Date, Transaction Date, or Posted Date.');
  if (descCol < 0) throw new Error('No Description column found. Expected: Description, Merchant, Name, or Payee.');
  if (amtCol < 0 && (debitCol < 0 || creditCol < 0))
    throw new Error('No Amount column found. Expected: Amount, or separate Debit/Credit columns.');

  const normalizeDate = (raw) => {
    if (!raw) return null;
    // Already YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
    // MM/DD/YYYY or DD/MM/YYYY → assume MM/DD/YYYY (North American)
    const m = raw.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);
    if (m) {
      const year = m[3].length === 2 ? '20' + m[3] : m[3];
      return `${year}-${m[1].padStart(2, '0')}-${m[2].padStart(2, '0')}`;
    }
    return raw;
  };

  const parseMoney = (s) => parseFloat((s || '').replace(/[$, \t]/g, '')) || 0;

  const byAccount = {};
  for (let i = 1; i < lines.length; i++) {
    const row = parseCSVRow(lines[i]);
    if (row.every(c => !c)) continue;

    const date = normalizeDate(row[dateCol]?.trim());
    if (!date) continue;

    const description = row[descCol]?.trim() || '(no description)';
    const category    = catCol >= 0 ? (row[catCol]?.trim() || 'Other') : 'Other';
    const acct        = acctCol >= 0 ? (row[acctCol]?.trim() || 'chequing') : 'chequing';
    const balRaw      = balCol >= 0 ? parseMoney(row[balCol]) : null;

    let amount;
    if (amtCol >= 0) {
      amount = parseMoney(row[amtCol]);
    } else {
      const debit  = parseMoney(row[debitCol]);
      const credit = parseMoney(row[creditCol]);
      amount = credit > 0 ? credit : -debit;
    }

    const txn = { date, description, amount, category };
    if (balRaw != null && !isNaN(balRaw)) txn.balance_after = balRaw;

    if (!byAccount[acct]) byAccount[acct] = [];
    byAccount[acct].push(txn);
  }

  if (Object.keys(byAccount).length === 0) throw new Error('No valid transactions found — check that the CSV has valid Date and Amount values.');

  for (const txns of Object.values(byAccount)) txns.sort((a, b) => a.date.localeCompare(b.date));

  const allDates = Object.values(byAccount).flat().map(t => t.date).sort();
  const dataWindow = `${allDates[0]} to ${allDates[allDates.length - 1]}`;

  const accounts = {};
  for (const [acctName, txns] of Object.entries(byAccount)) {
    const key = acctName.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '') || 'chequing';
    const lastBal = [...txns].reverse().find(t => t.balance_after != null)?.balance_after ?? 0;
    accounts[key] = { account_type: guessAccountType(acctName), current_balance: lastBal, currency: 'CAD', transactions: txns };
  }

  const displayName = filename.replace(/\.[^.]+$/, '').replace(/[_\-]+/g, ' ');
  return {
    name: displayName || 'CSV Upload',
    profile: { data_window: dataWindow, customer: { customer_id: `csv-${Date.now()}`, first_name: 'CSV', last_name: 'Upload' }, accounts, goals: [] },
  };
}
// ─────────────────────────────────────────────────────────────────────────────

function App() {
    // Screen state + a history stack so the in-screen back button can
    // return to the previous screen rather than a hard-coded target.
    //
    // History is a ref, not state. An earlier version stored history in
    // useState and pushed inside the state updater of setCurrentScreenRaw
    // — but React StrictMode invokes state updaters twice for purity
    // checking, which caused duplicate history entries and seemingly
    // random back-button cycles. A ref-based stack lets us read/write the
    // history without triggering re-renders (we don't need history to be
    // reactive — only screen is).
    // Land on the real Home screen (Today feed + balance hero) — the same
    // screen both the bottom-nav "Home" tab and the demo-nav "Home" button
    // point to. Previously this defaulted to 'accounts' (AccountsScreen), which
    // also maps to the Home tab, so the app opened on a look-alike screen with
    // no Today feed until you tapped Home again.
    const [currentScreen, setCurrentScreenRaw] = useState('home');
    const currentScreenRef = useRef('home');
    const historyRef       = useRef([]);

    const setCurrentScreen = useCallback((next) => {
      const prev     = currentScreenRef.current;
      const computed = typeof next === 'function' ? next(prev) : next;
      if (computed === prev) return;
      historyRef.current = [...historyRef.current, prev];
      currentScreenRef.current = computed;
      setCurrentScreenRaw(computed);
    }, []);

    const goBack = useCallback(() => {
      const stack = historyRef.current;
      if (stack.length === 0) {
        currentScreenRef.current = 'home';
        setCurrentScreenRaw('home');
        return;
      }
      const target = stack[stack.length - 1];
      historyRef.current = stack.slice(0, -1);
      currentScreenRef.current = target;
      setCurrentScreenRaw(target);
    }, []);

    // Chat-seed handoff: when a screen wants to start a chat with a
    // specific opening question, it calls openChatWith(text). The seed is
    // passed to ChatScreen, which auto-sends it as the user's first turn,
    // then calls onSeedConsumed to clear it.
    const [pendingChatSeed, setPendingChatSeed] = useState(null);
    const openChatWith = useCallback((seedText) => {
      if (seedText && typeof seedText === 'string') setPendingChatSeed(seedText);
      setCurrentScreen('chat');
    }, [setCurrentScreen]);

    // Load a profile payload to the server session cache.
    // Called by ChatScreen when chatProfilePayload changes.
    // Returns sessionId + sessionSummary; stores them in App state for context.
    const loadProfileSession = useCallback(async (chatProfilePayload) => {
      if (!chatProfilePayload) {
        setSessionId(null);
        setSessionSummary(null);
        return;
      }
      try {
        console.log('[Session] calling loadProfileToServer, payload source:', chatProfilePayload?.source);
        const result = await loadProfileToServer(chatProfilePayload, sessionId);
        console.log('[Session] loadProfile resolved to:', result);
        if (!result?.sessionId) {
          console.warn('[Session] Profile load returned unexpected response:', result);
          setSessionId(null);
          setSessionSummary(null);
          return;
        }
        setSessionId(result.sessionId);
        setSessionSummary(result.sessionSummary);
      } catch (err) {
        console.warn('[Session] Profile load failed, will fall back to inline profileData:', err.message);
        setSessionId(null);
        setSessionSummary(null);
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [selectedAccountId, setSelectedAccountId] = useState(null);
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [advisorRequest, setAdvisorRequest] = useState(null); // captured request text for the booking screen
    const [loadedProfileData, setLoadedProfileData] = useState(null); // Track loaded profile data
    const [sessionId, setSessionId] = useState(null);        // Server-side profile session cache key
    const [sessionSummary, setSessionSummary] = useState(null); // Pre-computed profile summary (~2-4KB)

    // Establish the server-side profile session as soon as a profile is loaded —
    // not only when ChatScreen first mounts. The Home "Today" feed (and other
    // session-gated surfaces) need `sessionId`, so waiting for the user to open
    // Chat meant the Today card never appeared if they stayed on Home. A ref
    // guards against re-loading the same profile on unrelated re-renders.
    const lastSessionSourceRef = useRef(null);
    useEffect(() => {
      if (!loadedProfileData) {
        lastSessionSourceRef.current = null;
        setSessionId(null);
        setSessionSummary(null);
        return;
      }
      const src = loadedProfileData.source ?? loadedProfileData.name ?? null;
      if (src && src === lastSessionSourceRef.current) return; // already loaded this profile
      lastSessionSourceRef.current = src;
      loadProfileSession(loadedProfileData);
    }, [loadedProfileData, loadProfileSession]);
    const [selectedBackend, setSelectedBackend] = useState('cohere'); // 'cohere' | 'openrouter' | 'claude' | 'ollama' | 'omlx'
    const [selectedModel, setSelectedModel] = useState('command-r-08-2024');
    const [omlxNoThink, setOmlxNoThink] = useState(true);
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [profileDropdownOpen, setProfileDropdownOpen] = useState(null); // null | 'float' | 'demo'
    const [globalSaveStatus, setGlobalSaveStatus] = useState(null); // null | 'saving' | 'saved' | 'error'
    const [isMobile, setIsMobile] = useState(() => {
      if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
        const mq = window.matchMedia('(max-width: 600px)');
        return mq && typeof mq.matches === 'boolean' ? mq.matches : false;
      }
      return false;
    });
    const [demoDrawerOpen, setDemoDrawerOpen] = useState(false);
    // Mobile DEV pulldown tab: draggable left/right (anchored top, persisted),
    // single-tap opens the drawer, double-tap collapses it to a thin line.
    const [demoTabX, setDemoTabX] = useState(() => {
      const v = Number(typeof localStorage !== 'undefined' && localStorage.getItem('iva.demoTabX'));
      return Number.isFinite(v) && v > 0 ? v : null; // null → CSS default (top-center)
    });
    const [demoTabCollapsed, setDemoTabCollapsed] = useState(false);
    const demoDrag = useRef({ dragging: false, moved: false, startX: 0, origLeft: 0, width: 0, x: null, lastTap: 0, tapTimer: null });
    // Mirror the collapsed flag into a ref so the (deps-free) pointer-up handler
    // reads the current value — needed to branch tap behaviour while collapsed.
    const demoCollapsedRef = useRef(demoTabCollapsed);
    useEffect(() => { demoCollapsedRef.current = demoTabCollapsed; }, [demoTabCollapsed]);

    const onDemoTabPointerDown = useCallback((e) => {
      const d = demoDrag.current;
      const rect = e.currentTarget.getBoundingClientRect();
      d.dragging = true; d.moved = false; d.startX = e.clientX;
      d.origLeft = rect.left; d.width = rect.width;
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
    }, []);
    const onDemoTabPointerMove = useCallback((e) => {
      const d = demoDrag.current;
      if (!d.dragging) return;
      const dx = e.clientX - d.startX;
      if (Math.abs(dx) > 4) d.moved = true;
      if (!d.moved) return;
      const left = Math.max(4, Math.min(window.innerWidth - d.width - 4, d.origLeft + dx));
      d.x = left;
      setDemoTabX(left);
    }, []);
    const onDemoTabPointerUp = useCallback((e) => {
      const d = demoDrag.current;
      if (!d.dragging) return;
      d.dragging = false;
      try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) {}
      if (d.moved) { // a drag, not a tap — persist and stop
        if (d.x != null) { try { localStorage.setItem('iva.demoTabX', String(Math.round(d.x))); } catch (_) {} }
        return;
      }
      // While collapsed the tab is just a sliver — ANY tap restores the full tab
      // (it must NOT open the drawer, otherwise you can never expand it back).
      if (demoCollapsedRef.current) {
        if (d.tapTimer) { clearTimeout(d.tapTimer); d.tapTimer = null; }
        d.lastTap = 0;
        setDemoTabCollapsed(false);
        return;
      }
      const now = Date.now();
      if (now - d.lastTap < 300) { // double-tap → collapse to a sliver
        d.lastTap = 0;
        if (d.tapTimer) { clearTimeout(d.tapTimer); d.tapTimer = null; }
        setDemoTabCollapsed(true);
      } else { // single tap (deferred so a second tap can cancel it) → open drawer
        d.lastTap = now;
        d.tapTimer = setTimeout(() => { d.tapTimer = null; setDemoDrawerOpen(v => !v); }, 300);
      }
    }, []);
    // Login-required is controlled server-side only (admin-set), not by the
    // client — see /api/config/login-required. Start optimistic (not required)
    // and let the fetch below correct it, avoiding a flash of the login screen.
    const [loginRequired, setLoginRequired] = useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState(true);
    const [loginConfigLoaded, setLoginConfigLoaded] = useState(false);
    const [currentUser, setCurrentUser] = useState('');
    // isAdmin: true when logged in as an admin user, or when login is not required (dev/demo mode).
    const [isAdmin, setIsAdmin] = useState(false);
    useEffect(() => {
      fetch('/api/config/login-required')
        .then(r => r.json())
        .then(({ loginRequired: required }) => {
          setLoginRequired(Boolean(required));
          // If login is now required and user isn't authenticated, force login screen.
          if (required) setIsLoggedIn(false);
          else setIsAdmin(true); // dev/demo mode — full access
        })
        .catch(() => {
          // Fetch failed (offline, error) — default to no login required (open demo mode).
          setIsAdmin(true);
        })
        .finally(() => setLoginConfigLoaded(true));
    }, []);
    const handleToggleLoginRequired = useCallback(async (newValue) => {
      try {
        const res = await fetch('/api/admin/config/login-required', {
          method: 'POST',
          headers: authHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({ loginRequired: newValue }),
        });
        const data = await res.json();
        if (data.success) setLoginRequired(data.loginRequired);
      } catch {
        // silently ignore — UI stays as-is
      }
    }, []);
    const [showUpcoming, setShowUpcoming] = useState(() => {
      try { return window.localStorage.getItem('demoShowUpcoming') === 'true'; } catch { return false; }
    });
    useEffect(() => {
      try { window.localStorage.setItem('demoShowUpcoming', String(showUpcoming)); } catch {}
    }, [showUpcoming]);
    const [loginLoading, setLoginLoading] = useState(false);
    const [loginError, setLoginError] = useState(null);
    // Gate screen mode: 'login' | 'register'. Self-serve guests register here
    // and land in a pending state until an admin approves them.
    const [authMode, setAuthMode] = useState('login');
    const [authNotice, setAuthNotice] = useState(null); // success/info banner (e.g. pending approval)
    const [resetDone, setResetDone] = useState(false);
    const phoneViewportRef = useRef(null);
    const [voiceLogs, setVoiceLogs] = useState([]);
    const voiceLogEndRef = useRef(null);
    const [ollamaNoThink, setOllamaNoThink] = useState(true);
    // Live model lists fetched from /api/models, keyed by backend id.
    const [providerModels, setProviderModels] = useState({});
    const [voiceLogMinimized, setVoiceLogMinimized] = useState(false);
    const [voiceLogWidth, setVoiceLogWidth] = useState(450);
    // Console is fixed to top-right below the toolbar buttons; only left-edge is resizable.
    const LOG_FILTER_GROUPS = { Voice: ['user','tts'], LLM: ['bot'], API: ['api'], System: ['system','status'], Errors: ['error'] };
    const [voiceLogFilters, setVoiceLogFilters] = useState(() => new Set(Object.keys(LOG_FILTER_GROUPS)));
    const [voiceLogVisible, setVoiceLogVisible] = useState(false);
    const [voiceLogTab, setVoiceLogTab] = useState('log'); // 'log' | 'prompt' | 'context' | 'memory' | 'scenario'
    const [simDate, setSimDate] = useState(null); // null = live; YYYY-MM-DD = sim mode active

    // Customer memory inspector (console MEMORY tab) — what IVA has learned.
    const [memoryData, setMemoryData] = useState(null);
    const [memoryLoading, setMemoryLoading] = useState(false);
    const memoryScrollRef = useRef(null);
    const refreshMemory = useCallback(() => {
      const cid = sessionSummary?.customerId;
      if (!cid) { setMemoryData(null); return; }
      setMemoryLoading(true);
      getMemory(cid).then(m => { setMemoryData(m); setMemoryLoading(false); });
    }, [sessionSummary?.customerId]);
    // Auto-load whenever the MEMORY tab is opened (or the customer changes while open).
    useEffect(() => {
      if (voiceLogVisible && voiceLogTab === 'memory') refreshMemory();
    }, [voiceLogVisible, voiceLogTab, refreshMemory]);
    // Bumped by handleClearMemory so memory-derived UI refetches without relying on sessionId.
    const [memoryVersion, setMemoryVersion] = useState(0);
    // Reload when memory is explicitly cleared (memoryVersion increments in handleClearMemory).
    // Using an effect instead of a direct call so it always runs with a fresh closure,
    // avoiding the stale-useCallback problem.
    useEffect(() => {
      if (memoryVersion === 0) return;
      setMemoryData(null);
      setMemoryLoading(true);
      const cid = sessionSummary?.customerId;
      if (cid) getMemory(cid).then(m => { setMemoryData(m); setMemoryLoading(false); });
    }, [memoryVersion]); // eslint-disable-line react-hooks/exhaustive-deps
    // Auto-refresh memory every 20s while the tab is open so IVA's learning
    // is visible in real-time without needing a manual tab switch.
    useEffect(() => {
      if (!voiceLogVisible || voiceLogTab !== 'memory') return;
      const id = setInterval(refreshMemory, 20000);
      return () => clearInterval(id);
    }, [voiceLogVisible, voiceLogTab, refreshMemory]);

    // Scroll to top when fresh memory data loads.
    useEffect(() => {
      if (memoryData && memoryScrollRef.current) memoryScrollRef.current.scrollTop = 0;
    }, [memoryData]);

    // ── Prompt editor state ────────────────────────────────────────────────────
    // Initialized immediately from localStorage (if the user previously saved edits)
    // or from DEFAULT_PROMPTS (hardcoded frontend copy of the server's BASE_SYSTEM_PROMPT).
    // Changes here rebuild composedSystemPrompt, which is passed as customSystemPrompt on
    // every LLM call — so edits take effect on the very next message with no page reload.
    const _savedPrompts = loadSavedPrompts(); // null if nothing saved yet
    const [promptSystemPrompt,  setPromptSystemPrompt]  = useState(_savedPrompts?.identity     ?? DEFAULT_PROMPTS.identity);
    const [promptScope,         setPromptScope]         = useState(_savedPrompts?.scope        ?? DEFAULT_PROMPTS.scope);
    const [promptGuidelines,    setPromptGuidelines]    = useState(_savedPrompts?.generalRules ?? DEFAULT_PROMPTS.generalRules);
    const [promptSafety,        setPromptSafety]        = useState(_savedPrompts?.safety       ?? DEFAULT_PROMPTS.safety);
    const [promptOutputFormat,  setPromptOutputFormat]  = useState(_savedPrompts?.outputFormat ?? DEFAULT_PROMPTS.outputFormat);
    const [promptEscalation,    setPromptEscalation]    = useState(_savedPrompts?.escalation   ?? DEFAULT_PROMPTS.escalation);
    const [promptFollowUp,      setPromptFollowUp]      = useState(_savedPrompts?.followUp     ?? DEFAULT_PROMPTS.followUp);
    const DEFAULT_SUMMARIZE_PROMPT = `Summarize the following assistant reply in 1–4 short spoken sentences. Write only the summary — no preamble, no labels, no markdown. Make it natural to say out loud.`;
    const [summarizePrompt, setSummarizePrompt] = useState(() => {
      try { return localStorage.getItem('iva_summarize_prompt') ?? DEFAULT_SUMMARIZE_PROMPT; } catch { return DEFAULT_SUMMARIZE_PROMPT; }
    });
    // Track whether the current values differ from what's saved in localStorage.
    const [promptsSaved, setPromptsSaved] = useState(!!_savedPrompts);
    // The canonical defaults never change at runtime — used for diff display and field reset.
    const defaultPromptsRef = useRef(DEFAULT_PROMPTS);
    // Undo stack — up to 3 snapshots; ref holds data, undoDepth drives re-renders.
    const undoStackRef = useRef([]);
    const [undoDepth, setUndoDepth] = useState(0);
    const pushUndo = useCallback((snapshot) => {
      undoStackRef.current = [...undoStackRef.current.slice(-2), snapshot];
      setUndoDepth(undoStackRef.current.length);
    }, []);

    // Compose the full system prompt from all editable sections.
    // Always returns a non-empty string (fields start from DEFAULT_PROMPTS, never blank).
    // This is passed as customSystemPrompt on every LLM call, so edits take effect
    // on the very next message — no page reload needed.
    const composedSystemPrompt = useMemo(() => {
      const parts = [];
      if (promptSystemPrompt?.trim())  parts.push(`<identity>\n${promptSystemPrompt.trim()}\n</identity>`);
      if (promptScope?.trim())         parts.push(`<scope>\n${promptScope.trim()}\n</scope>`);
      if (promptGuidelines?.trim())    parts.push(`<rules>\n${promptGuidelines.trim()}\n</rules>`);
      if (promptSafety?.trim())        parts.push(`<safety>\n${promptSafety.trim()}\n</safety>`);
      if (promptOutputFormat?.trim())  parts.push(`<output_format>\n${promptOutputFormat.trim()}\n</output_format>`);
      if (promptEscalation?.trim())    parts.push(`<escalation>\n${promptEscalation.trim()}\n</escalation>`);
      if (promptFollowUp?.trim())      parts.push(`<follow_up>\n${promptFollowUp.trim()}\n</follow_up>`);
      // Return null only if the user has cleared every field (edge case), so the server
      // falls back to its own BASE_SYSTEM_PROMPT rather than sending an empty string.
      return parts.length > 0 ? parts.join('\n\n') : null;
    }, [promptSystemPrompt, promptScope, promptGuidelines, promptSafety, promptOutputFormat, promptEscalation, promptFollowUp]);

    // Voice / TTS settings (lifted so demo nav can control them)
    const [ttsEngine,        setTtsEngine]        = useState('browser');
    const [elVoiceId,        setElVoiceId]        = useState(ELEVENLABS_DEFAULT_VOICE);
    const [openaiVoiceId,    setOpenaiVoiceId]    = useState(OPENAI_DEFAULT_VOICE);
    const [qwenVoiceId,      setQwenVoiceId]      = useState('ryan');
    const [voiceOut,         setVoiceOut]         = useState(true);
    const [summarizeProvider, setSummarizeProvider] = useState('same');

    const addVoiceLog = useCallback((type, message) => {
      const now = new Date();
      const time = now.toLocaleTimeString('en-CA', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
      setVoiceLogs(prev => [...prev.slice(-200), { id: Date.now() + Math.random(), time, type, message }]);
    }, []);

    const handleLogin = async (e) => {
      e.preventDefault();
      const form = e.target;
      const username = form.username.value.trim();
      const password = form.password.value.trim();
      if (!username || !password) {
        setLoginError('Username and password are required.');
        return;
      }
      setLoginLoading(true);
      setLoginError(null);

      const clientInfo = {
        screen:   `${window.screen.width}x${window.screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        platform: navigator.platform || navigator.userAgentData?.platform || 'unknown',
        language: navigator.language,
      };

      try {
        const res = await fetch('/api/auth/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username, password, clientInfo }),
        });
        const data = await res.json();
        if (!res.ok || !data.success) {
          setLoginError(data.error || 'Login failed. Please try again.');
          setLoginLoading(false);
          return;
        }
        // Pending guests authenticate but have no paid access yet — keep them at
        // the gate with a clear notice rather than dropping them into a half-
        // working app. They get in once an admin approves them.
        if (data.status === 'pending' && data.isAdmin !== true) {
          setAuthNotice('Your account is awaiting administrator approval. You\'ll be able to sign in once it\'s enabled.');
          setLoginLoading(false);
          return;
        }
        setAuthToken(data.token || null);
        setIsLoggedIn(true);
        setIsAdmin(data.isAdmin === true);
        setCurrentUser(data.username || username);
        // Admin issued a temp password → force them to set their own before use.
        if (data.mustChangePassword) {
          setMustChangePw(true);
          setCurrentScreen('changePassword');
        }
      } catch {
        setLoginError('Could not reach the server. Please try again.');
      }
      setLoginLoading(false);
    };

    const handleRegister = async (e) => {
      e.preventDefault();
      const form = e.target;
      const username = form.username.value.trim();
      const password = form.password.value.trim();
      if (!username || !password) {
        setLoginError('Username and password are required.');
        return;
      }
      if (password.length < 6) {
        setLoginError('Password must be at least 6 characters.');
        return;
      }
      setLoginLoading(true);
      setLoginError(null);
      setAuthNotice(null);
      try {
        const res = await fetch('/api/auth/register', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username, password }),
        });
        const data = await res.json();
        if (!res.ok || !data.success) {
          setLoginError(data.error || 'Registration failed. Please try again.');
          setLoginLoading(false);
          return;
        }
        setAuthMode('login');
        setAuthNotice(data.message || 'Account created — awaiting administrator approval.');
      } catch {
        setLoginError('Could not reach the server. Please try again.');
      }
      setLoginLoading(false);
    };

    const handleRequestReset = async (username) => {
      setLoginLoading(true);
      setLoginError(null);
      setAuthNotice(null);
      try {
        const res = await requestPasswordReset(username);
        if (!res.success) {
          setLoginError(res.error || 'Could not send your request. Please try again.');
          setLoginLoading(false);
          return;
        }
        // Generic by design — never reveals whether the account exists.
        setResetDone(true);
      } catch {
        setLoginError('Could not reach the server. Please try again.');
      }
      setLoginLoading(false);
    };

    // ── Change-password modal ──────────────────────────────────────────────────
    const [changePwLoading, setChangePwLoading] = useState(false);
    const [changePwError, setChangePwError] = useState(null);
    const [changePwDone, setChangePwDone] = useState(false);
    // Set when an admin-issued temp password requires the user to pick their own
    // before using the app. While true (and not yet done) the modal can't be dismissed.
    const [mustChangePw, setMustChangePw] = useState(false);
    const closeChangePw = () => {
      setChangePwError(null);
      setChangePwDone(false);
      setChangePwLoading(false);
      setMustChangePw(false);
      goBack();
    };
    const handleChangePassword = async ({ currentPassword, newPassword, confirmPassword }) => {
      if (!currentPassword || !newPassword) {
        setChangePwError('All fields are required.');
        return;
      }
      if (newPassword.length < 6) {
        setChangePwError('New password must be at least 6 characters.');
        return;
      }
      if (newPassword !== confirmPassword) {
        setChangePwError('New passwords do not match.');
        return;
      }
      setChangePwLoading(true);
      setChangePwError(null);
      const result = await changePassword({ username: currentUser, currentPassword, newPassword });
      setChangePwLoading(false);
      if (!result.success) {
        setChangePwError(result.error || 'Could not change password.');
        return;
      }
      setChangePwDone(true);
    };

    const handleLogout = async () => {
      try {
        await fetch('/api/auth/logout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username: currentUser }),
        });
      } catch { /* best-effort */ }
      setAuthToken(null);
      setIsLoggedIn(false);
      setCurrentUser('');
      setIsAdmin(false);
      setMustChangePw(false);
    };

    useEffect(() => {
      const mq = window.matchMedia('(max-width: 600px)');
      const handler = e => setIsMobile(e.matches);
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    }, []);

    useEffect(() => {
      // When the provider changes, select its sensible default immediately…
      const def = PROVIDER_DEFAULT_MODEL[selectedBackend];
      if (def) setSelectedModel(def);

      // …then pull the live model list from the provider and refresh the dropdown.
      // Falls back to MODEL_FALLBACKS in render if this fails (no key / unreachable).
      let cancelled = false;
      fetch(`/api/models?provider=${encodeURIComponent(selectedBackend)}`)
        .then(r => r.ok ? r.json() : { models: [] })
        .then(data => {
          if (cancelled) return;
          const models = (data.models || []).map(m => ({ value: m.name, label: m.label || m.name }));
          if (!models.length) return;
          setProviderModels(prev => ({ ...prev, [selectedBackend]: models }));
          // Keep the default if the provider actually offers it; else first live model.
          setSelectedModel(prev => models.some(m => m.value === prev) ? prev : models[0].value);
        })
        .catch(() => {});
      return () => { cancelled = true; };
    }, [selectedBackend]);

    // Render the model <option> list for a backend — live list if fetched, else fallback.
    const renderModelOptions = useCallback((backend) => {
      const live = providerModels[backend];
      const isLive = Array.isArray(live) && live.length > 0;
      const list = isLive ? live : (MODEL_FALLBACKS[backend] || []);
      const name = BACKEND_LABEL[backend] || backend;
      return (
        <optgroup label={`── ${name} models${isLive ? ' (live)' : ''} ──`}>
          {list.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
        </optgroup>
      );
    }, [providerModels]);

    const PROFILE_OPTIONS = [
      { key: 'none',                 label: 'None',                sub: 'Clear profile' },
      { key: 'default',              label: 'Default',             sub: 'Demo data' },
      { key: 'ahmed-small',          label: 'Ahmed Al-Rashidi',    sub: 'Young tech. professional' },
      { key: 'sarah-chen',           label: 'Sarah Chen',          sub: 'Everyday earner & gig worker' },
      { key: 'jason-jones',          label: 'Jason Jones',         sub: "High earner — can't seem to save" },
      { key: 'sam-okafor',           label: 'Sam Okafor',          sub: 'Divorced business owner' },
      { key: 'margaret-whitfield',   label: 'Margaret Whitfield',  sub: 'Private wealth — investor' },
      { key: 'priya-patel',          label: 'Priya Patel',         sub: 'Family doing well' },
      { key: 'marcus-hill',          label: 'Marcus Hill',         sub: 'Struggling financially' },
      { key: 'justin-lee',           label: 'Justin Lee',          sub: 'Young family — tight cash flow' },
    ];

    const loadProfile = useCallback((key) => {
      setProfileDropdownOpen(null);
      if (key === 'none') { setLoadedProfileData(null); return; }
      let profileDataToStore = null;
      if (key === 'default') {
        profileDataToStore = { name: 'Default', goals: goalsData.goalsData, profile: defaultProfile, source: 'default' };
      } else if (key === 'ahmed-small') {
        profileDataToStore = { name: 'Ahmed Al-Rashidi', goals: goalsData.goalsData, profile: ahmedSmall, source: 'ahmed-small' };
      } else if (key === 'sarah-chen') {
        profileDataToStore = { name: 'Sarah Chen', goals: sarahChen.goals || goalsData.goalsData, profile: sarahChen, source: 'sarah-chen' };
      } else if (key === 'jason-jones') {
        profileDataToStore = { name: 'Jason Jones', goals: jasonJones.goals || goalsData.goalsData, profile: jasonJones, source: 'jason-jones' };
      } else if (key === 'sam-okafor') {
        profileDataToStore = { name: 'Sam Okafor', goals: samOkafor.goals || goalsData.goalsData, profile: samOkafor, source: 'sam-okafor' };
      } else if (key === 'margaret-whitfield') {
        profileDataToStore = { name: 'Margaret Whitfield', goals: margaretWhitfield.goals || goalsData.goalsData, profile: margaretWhitfield, source: 'margaret-whitfield' };
      } else if (key === 'priya-patel') {
        profileDataToStore = { name: 'Priya Patel', goals: priyaPatel.goals || goalsData.goalsData, profile: priyaPatel, source: 'priya-patel' };
      } else if (key === 'marcus-hill') {
        profileDataToStore = { name: 'Marcus Hill', goals: marcusHill.goals || goalsData.goalsData, profile: marcusHill, source: 'marcus-hill' };
      } else if (key === 'justin-lee') {
        profileDataToStore = { name: 'Justin Lee', goals: justinLee.goals || goalsData.goalsData, profile: justinLee, source: 'justin-lee' };
      }
      if (profileDataToStore) setLoadedProfileData(profileDataToStore);
    }, []);

    const csvFileRef = useRef(null);
    const handleCsvFileChange = useCallback((e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      e.target.value = '';
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const { name, profile } = parseCsvToProfile(ev.target.result, file.name);
          setLoadedProfileData({ name, goals: [], profile, source: 'csv-upload' });
        } catch (err) {
          alert(`CSV import failed: ${err.message}`);
        }
      };
      reader.readAsText(file);
    }, []);

    // Clear IVA's persistent learned memory for the loaded customer (server re-bootstraps from transactions)
    const [memoryClearStatus, setMemoryClearStatus] = useState(null); // null | 'done' | 'error'
    const handleClearMemory = useCallback(async () => {
      const customerId = sessionSummary?.customerId;
      if (!customerId) return;
      if (!window.confirm(`Clear IVA's learned memory for ${loadedProfileData?.name || customerId}? Concepts, corrections and preferences reset to a fresh bootstrap.`)) return;
      try {
        setMemoryData(null);
        setMemoryLoading(true);
        await clearCustomerMemory(customerId);
        // Re-load the profile so the server re-bootstraps memory AND re-derives the
        // understanding snapshot. Background analysis (persona/health/assumptions)
        // only runs on /api/profile/load — a bare clear would leave chat with no
        // derived understanding until the next manual reload.
        if (loadedProfileData) await loadProfileSession(loadedProfileData);
        // Fetch directly with the local customerId variable — avoids stale-closure
        // issues that can occur when reading sessionSummary through the effect chain.
        const fresh = await getMemory(customerId);
        setMemoryData(fresh);
        setMemoryLoading(false);
        setMemoryVersion(v => v + 1); // bump so Finances assumptions card refetches
        setMemoryClearStatus('done');
      } catch (err) {
        console.warn('[Memory] Clear failed:', err.message);
        setMemoryLoading(false);
        setMemoryClearStatus('error');
      }
      setTimeout(() => setMemoryClearStatus(null), 2000);
    }, [sessionSummary, loadedProfileData, loadProfileSession]);

    const handleGlobalSave = useCallback(async () => {
      if (globalSaveStatus === 'saving') return;
      setGlobalSaveStatus('saving');
      // Override -apple-system on the LIVE document so the browser reflows
      // layout BEFORE html2canvas clones the DOM. Canvas cannot measure
      // -apple-system glyphs, collapsing word/letter spacing. We use
      // 'Helvetica Neue' first because it shares near-identical character
      // widths with SF Pro (-apple-system), preventing layout shifts.
      // Two rAF cycles ensure a complete repaint before cloning.
      document.documentElement.style.setProperty('font-family', "'Helvetica Neue', Helvetica, Arial, sans-serif", 'important');
      await new Promise(r => requestAnimationFrame(r));
      await new Promise(r => requestAnimationFrame(r));
      try {
        const el = phoneViewportRef.current;
        if (!el) throw new Error('Viewport not found');
        const now = new Date();
        const stamp = [
          now.getFullYear(),
          String(now.getMonth() + 1).padStart(2, '0'),
          String(now.getDate()).padStart(2, '0'),
          String(now.getHours()).padStart(2, '0'),
          String(now.getMinutes()).padStart(2, '0'),
          String(now.getSeconds()).padStart(2, '0'),
        ].join('-');
        const screenSlug = currentScreen.replace(/([A-Z])/g, c => `-${c.toLowerCase()}`);
        const filename = `screenshot_${stamp}_${screenSlug}.png`;
        const canvas = await html2canvas(el, {
          scale: window.devicePixelRatio || 2,
          useCORS: true,
          allowTaint: false,
          logging: false,
        });
        canvas.toBlob(blob => {
          const a = document.createElement('a');
          a.href = URL.createObjectURL(blob);
          a.download = filename;
          a.click();
          URL.revokeObjectURL(a.href);
        }, 'image/png');
        setGlobalSaveStatus('saved');
      } catch (e) {
        console.error('[handleGlobalSave]', e);
        setGlobalSaveStatus('error');
      } finally {
        document.documentElement.style.removeProperty('font-family');
      }
      setTimeout(() => setGlobalSaveStatus(null), 2000);
    }, [globalSaveStatus, currentScreen]);

    const buildDate = process.env.REACT_APP_BUILD_DATE || '2026-01-12 00:00:00';
    const version = '0.10.0';

    // THEME STATE — default to dark ('purple'); 'green' is the light theme.
    const [theme, setTheme] = useState('purple');

    // Background-analysis indicator: when a profile session opens, the server runs
    // the agent pipeline. Poll insights readiness and pulse the Insights tab until
    // results are ready, so the user knows IVA is working even off-screen.
    const [agentsRunning, setAgentsRunning] = useState(false);
    useEffect(() => {
      if (!sessionId) { setAgentsRunning(false); return; }
      let cancelled = false;
      let timer = null;
      setAgentsRunning(true);
      const poll = async () => {
        try {
          const res = await fetch(`/api/profile/insights/${sessionId}`);
          const data = res.ok ? await res.json() : null;
          if (cancelled) return;
          if (data?.ready) { setAgentsRunning(false); return; }
        } catch (_) { /* keep polling */ }
        if (!cancelled) timer = setTimeout(poll, 2500);
      };
      poll();
      return () => { cancelled = true; if (timer) clearTimeout(timer); };
    }, [sessionId]);

    // Dev-only test hook: lets the contrast audit (tests/contrast) drive the app
    // — jump to any screen and flip the theme — without URL routing. Never exposed
    // in a production build.
    useEffect(() => {
      if (process.env.NODE_ENV === 'production') return;
      window.__ivaTest = {
        setScreen: (name) => setCurrentScreen(name),
        getScreen: () => currentScreenRef.current,
        setTheme: (t) => setTheme(t),
        getTheme: () => theme,
        loadProfile: (key) => loadProfile(key),
        hasProfile: () => !!loadedProfileData,
        // Simulate a logged-in user so login-gated UI renders in screenshots.
        // Dev-only; no real auth/token — purely for the screenshot harness.
        setUser: (u) => setCurrentUser(u || ''),
        getUser: () => currentUser,
      };
      return () => { try { delete window.__ivaTest; } catch {} };
    }, [theme, loadProfile, loadedProfileData, currentUser]);

    // Global privacy toggle — when true, every screen masks the dollar
    // amount in balances/totals (account cards, hero, mortgage, portfolio,
    // subscriptions header). Persists across refreshes via localStorage so
    // the choice survives a reload.
    const [hideBalances, setHideBalances] = useState(() => {
      try { return window.localStorage.getItem('iva.hideBalances') === 'true'; }
      catch { return false; }
    });
    useEffect(() => {
      try { window.localStorage.setItem('iva.hideBalances', String(hideBalances)); } catch {}
    }, [hideBalances]);

    // CHAT HISTORY: saved past sessions + active in-progress conversation
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [chatEverToggled, setChatEverToggled] = useState(false);
    const [chatSessions, setChatSessions] = useState([]);
    const [sessionToLoad, setSessionToLoad] = useState(null);
    const [activeChatHistory, setActiveChatHistory] = useState(initialChatHistory);

    const handleSaveSession = (session) => {
      setChatSessions(prev => [...prev, session]);
    };

    const handleResumeSession = (session) => {
      setSessionToLoad(session);
      setChatEverToggled(true);
      setIsChatOpen(true);
    };

    // DASHBOARD: saved chat items
    const [savedDashboardItems, setSavedDashboardItems] = useState([]);

    const handlePinMessage = (msg) => {
      setSavedDashboardItems(prev => {
        // Toggle: if already saved, unpin it
        if (prev.some(item => item.id === msg.id)) {
          return prev.filter(item => item.id !== msg.id);
        }
        const now = new Date();
        const timestamp = now.toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
        return [...prev, { ...msg, timestamp }];
      });
    };

    const handleUnpinMessage = (id) => {
      setSavedDashboardItems(prev => prev.filter(item => item.id !== id));
    };

    // NEW STATES FOR THE FEATURE
    const [isCallStatusEnabled, setCallStatusEnabled] = useState(false); // Start as hidden
    const [callState, setCallState] = useState('none'); // 'active', 'recent', 'none'
    const timeoutRef = React.useRef(null);

    // Fullscreen mobile UX: ask for URL bar hide by scrolling on load/resize.
    useEffect(() => {
      const isMobile = /Mobi|Android|iPhone|iPad|iPod/.test(navigator.userAgent);
      if (!isMobile) return;
      const hideUrlBar = () => { window.scrollTo(0, 1); };
      hideUrlBar();
      window.addEventListener('resize', hideUrlBar);
      return () => window.removeEventListener('resize', hideUrlBar);
    }, []);

    // Effect to handle 30-second timeout for hiding banner
    useEffect(() => {
      // Clear existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // If there's no active call, hide banner after 30 seconds
      if (callState === 'none' && isCallStatusEnabled) {
        timeoutRef.current = setTimeout(() => {
          setCallStatusEnabled(false);
        }, 30000); // 30 seconds
      }

      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, [callState, isCallStatusEnabled]);

    // Scale phone viewport to fit the browser window using zoom (desktop only)
    useEffect(() => {
      const el = phoneViewportRef.current;
      if (!el) return;
      const update = () => {
        if (window.innerWidth <= 600) { el.style.zoom = ''; return; }
        const scaleH = (window.innerHeight - 48) / 828;
        const scaleW = (window.innerWidth - 240) / 391;
        const scale = Math.min(1, scaleH, scaleW);
        el.style.zoom = scale < 1 ? scale.toFixed(3) : '';
      };
      update();
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }, []);

    // Clamp console width when the viewport shrinks (e.g. browser devtools opens).
    useEffect(() => {
      const clamp = () => setVoiceLogWidth(w => Math.min(w, window.innerWidth - 20));
      window.addEventListener('resize', clamp);
      return () => window.removeEventListener('resize', clamp);
    }, []);

    // Save current prompt edits to localStorage so they survive page reload.
    const handleSavePrompts = () => {
      pushUndo({ identity: promptSystemPrompt, scope: promptScope, generalRules: promptGuidelines, safety: promptSafety, outputFormat: promptOutputFormat, escalation: promptEscalation, followUp: promptFollowUp, summarize: summarizePrompt, wasSaved: promptsSaved });
      const ok = savePromptsToStorage({
        identity: promptSystemPrompt,
        scope: promptScope,
        generalRules: promptGuidelines,
        safety: promptSafety,
        outputFormat: promptOutputFormat,
        escalation: promptEscalation,
        followUp: promptFollowUp,
      });
      try { localStorage.setItem('iva_summarize_prompt', summarizePrompt); } catch {}
      if (ok) setPromptsSaved(true);
    };

    // Reset all prompt fields to the hardcoded defaults and clear any saved state.
    const handleResetPrompts = () => {
      pushUndo({ identity: promptSystemPrompt, scope: promptScope, generalRules: promptGuidelines, safety: promptSafety, outputFormat: promptOutputFormat, escalation: promptEscalation, followUp: promptFollowUp, summarize: summarizePrompt, wasSaved: promptsSaved });
      setPromptSystemPrompt(DEFAULT_PROMPTS.identity);
      setPromptScope(DEFAULT_PROMPTS.scope);
      setPromptGuidelines(DEFAULT_PROMPTS.generalRules);
      setPromptSafety(DEFAULT_PROMPTS.safety);
      setPromptOutputFormat(DEFAULT_PROMPTS.outputFormat);
      setPromptEscalation(DEFAULT_PROMPTS.escalation);
      setPromptFollowUp(DEFAULT_PROMPTS.followUp);
      setSummarizePrompt(DEFAULT_SUMMARIZE_PROMPT);
      try { localStorage.removeItem('iva_summarize_prompt'); } catch {}
      clearSavedPrompts();
      setPromptsSaved(false);
    };

    const handleUndo = () => {
      const stack = undoStackRef.current;
      if (stack.length === 0) return;
      const b = stack[stack.length - 1];
      undoStackRef.current = stack.slice(0, -1);
      setUndoDepth(undoStackRef.current.length);
      setPromptSystemPrompt(b.identity);
      setPromptScope(b.scope);
      setPromptGuidelines(b.generalRules);
      setPromptSafety(b.safety);
      setPromptOutputFormat(b.outputFormat);
      setPromptEscalation(b.escalation);
      setPromptFollowUp(b.followUp);
      setSummarizePrompt(b.summarize);
      if (b.wasSaved) {
        savePromptsToStorage({ identity: b.identity, scope: b.scope, generalRules: b.generalRules, safety: b.safety, outputFormat: b.outputFormat, escalation: b.escalation, followUp: b.followUp });
        try { localStorage.setItem('iva_summarize_prompt', b.summarize); } catch {}
        setPromptsSaved(true);
      } else {
        clearSavedPrompts();
        try { localStorage.removeItem('iva_summarize_prompt'); } catch {}
        setPromptsSaved(false);
      }
    };

    // Mark prompts as unsaved whenever any field changes after an explicit save.
    useEffect(() => { setPromptsSaved(false); }, [promptSystemPrompt, promptScope, promptGuidelines, promptSafety, promptOutputFormat, promptEscalation, promptFollowUp, summarizePrompt]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-scroll voice log panel
    useEffect(() => { voiceLogEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [voiceLogs]);

    // Auto-hide the FAB-opened chat modal whenever the underlying screen
    // changes (demo nav, sidebar, deep links, bottom nav, etc.). The user
    // navigated away — they don't want the modal blocking the new screen.
    useEffect(() => { setIsChatOpen(false); }, [currentScreen]);

    // Clear voice log when leaving the conversation surfaces (chat / voiceBot
    // dedicated route, or chat modal over another screen). Default the panel
    // expanded on every entry — user can minimize via the panel chrome.
    useEffect(() => {
      const onConversation = currentScreen === 'voiceBot' || currentScreen === 'chat' || isChatOpen;
      if (!onConversation) setVoiceLogs([]);
      if (onConversation) setVoiceLogMinimized(false);
    }, [currentScreen, isChatOpen]);

    // Auto-save chat session whenever the chat modal is closed
    const prevChatOpenRef = React.useRef(isChatOpen);
    const prevScreenRef = React.useRef(currentScreen);
    const activeChatHistoryRef = React.useRef(activeChatHistory);
    activeChatHistoryRef.current = activeChatHistory;

    const saveActiveChatSession = () => {
      const history = activeChatHistoryRef.current;
      const userMessages = history.filter(m => m.isUser);
      if (userMessages.length > 0) {
        const sessionId = userMessages[0].id;
        const preview = userMessages[0].text.slice(0, 70);
        const timestamp = new Date().toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
        setChatSessions(prev => {
          const session = { id: sessionId, history: [...history], preview, timestamp, messageCount: userMessages.length };
          const idx = prev.findIndex(s => s.id === sessionId);
          if (idx >= 0) {
            const updated = [...prev];
            updated[idx] = session;
            return updated;
          }
          return [session, ...prev];
        });
      }
    };

    // Save when chat modal is closed
    useEffect(() => {
      const wasOpen = prevChatOpenRef.current;
      prevChatOpenRef.current = isChatOpen;
      if (wasOpen && !isChatOpen) saveActiveChatSession();
    }, [isChatOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    // Save when navigating away from the dedicated chat screen
    useEffect(() => {
      const prevScreen = prevScreenRef.current;
      prevScreenRef.current = currentScreen;
      if (prevScreen === 'chat' && currentScreen !== 'chat') saveActiveChatSession();
    }, [currentScreen]); // eslint-disable-line react-hooks/exhaustive-deps

    // Enhanced navigation handler to support passing data
    const handleNavigate = (screen, data) => {
      if (screen === 'voiceBot') setIsChatOpen(false);
      if (screen === 'accountDetails') {
        setSelectedAccountId(data);
        setCurrentScreen('accountDetails');
      } else if (screen === 'transactionDetail') {
        setSelectedTransaction(data);
        setCurrentScreen('transactionDetail');
      } else if (screen === 'bookAppointment') {
        setAdvisorRequest(typeof data === 'string' ? data : null);
        setCurrentScreen('bookAppointment');
      } else {
        setCurrentScreen(screen);
      }
    };

    const renderScreen = () => {
      switch (currentScreen) {
        case 'home':          return <HomeScreen onNavigate={handleNavigate} onBack={goBack} />;
        case 'accounts':      return <AccountsScreen onNavigate={handleNavigate} onBack={goBack} />;
        case 'accountDetails': return <AccountDetailsScreen onNavigate={handleNavigate} onBack={goBack} accountId={selectedAccountId} />;
        case 'search':        return <SearchScreen onNavigate={setCurrentScreen} onBack={goBack} />;
        case 'credit':        return <CreditScreen onNavigate={setCurrentScreen} onBack={goBack} />;
        case 'locations':     return <LocationsScreen onNavigate={setCurrentScreen} onBack={goBack} />;
        case 'receipt':       return <ReceiptScreen onNavigate={setCurrentScreen} onBack={goBack} />;
        case 'chat':          return <ChatScreen onNavigate={handleNavigate} onBack={goBack} onSaveToDashboard={handlePinMessage} pinnedMessageIds={savedDashboardItems.map(i => i.id)} onSaveSession={handleSaveSession} sessionToLoad={sessionToLoad} activeHistory={activeChatHistory} onHistoryChange={setActiveChatHistory} onLog={addVoiceLog} chatSeed={pendingChatSeed} onSeedConsumed={() => setPendingChatSeed(null)} />;
        case 'chatHistory':   return <ChatHistoryScreen onNavigate={setCurrentScreen} onBack={goBack} sessions={chatSessions} onResumeSession={handleResumeSession} />;
        case 'dashboard':     return <DashboardScreen onNavigate={setCurrentScreen} onBack={goBack} savedItems={savedDashboardItems} onUnpin={handleUnpinMessage} />;
        case 'cardMgmt':      return <CardManagementScreen onNavigate={setCurrentScreen} onBack={goBack} />;
        case 'security':      return <SecurityScreen onNavigate={setCurrentScreen} onBack={goBack} />;
        case 'call':          return <InAppCallScreen onNavigate={setCurrentScreen} onBack={goBack} />;
        case 'callVerify':    return <CallVerificationScreen onNavigate={setCurrentScreen} onBack={goBack} />;
        case 'depositDetail': return <DirectDepositDetailScreen onNavigate={setCurrentScreen} onBack={goBack} />;
        case 'spendingAnalysis': return <SpendingAnalysisScreen onNavigate={setCurrentScreen} onBack={goBack} />;
        case 'spendingScreen': return <SpendingScreen onNavigate={setCurrentScreen} onBack={goBack} />;
        case 'portfolio':     return <PortfolioScreen onNavigate={handleNavigate} onBack={goBack} />;
        case 'registeredAccounts': return <RegisteredAccountsScreen onNavigate={handleNavigate} onBack={goBack} />;
        case 'goalsScreen':   return <GoalsScreen onNavigate={setCurrentScreen} onBack={goBack} />;
        case 'insightsScreen': return <InsightsScreen onNavigate={setCurrentScreen} onBack={goBack} />;
        case 'agents':        return <AgentsScreen onNavigate={setCurrentScreen} onBack={goBack} />;
        case 'admin':         return <AdminScreen onNavigate={setCurrentScreen} onBack={goBack} />;
        case 'finances':      return <FinancesScreen onNavigate={setCurrentScreen} onBack={goBack} onChatHandoff={openChatWith} />;
        case 'financialSummary': return <FinancialSummaryScreen onNavigate={setCurrentScreen} onBack={goBack} />;
        case 'budgetRule':    return <BudgetRuleScreen onNavigate={setCurrentScreen} onBack={goBack} />;
        case 'budgetScreen':  return <BudgetScreen onNavigate={setCurrentScreen} onBack={goBack} onChatHandoff={openChatWith} />;
        case 'scenario':      return <ScenarioScreen onNavigate={setCurrentScreen} onBack={goBack} />;
        // Option A merger: voice → ChatScreen with voice mode auto-on. The
        // standalone VoiceBotScreen remains in the codebase as a fallback
        // (and a reference for the future Option C 'hybrid' layout).
        case 'voiceBot':       return <ChatScreen onNavigate={setCurrentScreen} onBack={goBack} onSaveToDashboard={handlePinMessage} pinnedMessageIds={savedDashboardItems.map(i => i.id)} onSaveSession={handleSaveSession} sessionToLoad={sessionToLoad} activeHistory={activeChatHistory} onHistoryChange={setActiveChatHistory} onLog={addVoiceLog} initialVoiceMode={true} />;
        case 'profileScreen':  return <ProfileScreen onNavigate={setCurrentScreen} onBack={goBack} />;
        case 'services':        return <ServicesHubScreen onNavigate={handleNavigate} onBack={goBack} onChatHandoff={openChatWith} />;
        case 'emailTransfer':   return <EmailMoneyTransferScreen onNavigate={setCurrentScreen} onBack={goBack} />;
        case 'billPayment':     return <BillPaymentScreen onNavigate={setCurrentScreen} onBack={goBack} />;
        case 'accountTransfer': return <AccountTransferScreen onNavigate={setCurrentScreen} onBack={goBack} />;
        case 'mobileDeposit':   return <MobileDepositScreen onNavigate={setCurrentScreen} onBack={goBack} />;
        case 'crossBorder':     return <CrossBorderScreen onNavigate={setCurrentScreen} onBack={goBack} />;
        case 'autoServices':    return <AutoServicesScreen onNavigate={setCurrentScreen} onBack={goBack} />;
        case 'preAuth':         return <PreAuthorizedScreen onNavigate={setCurrentScreen} onBack={goBack} />;
        case 'todaysRates':     return <TodaysRatesScreen onNavigate={setCurrentScreen} onBack={goBack} />;
        case 'mortgages':       return <MortgagesScreen onNavigate={setCurrentScreen} onBack={goBack} />;
        case 'openAccount':     return <OpenAccountsScreen onNavigate={setCurrentScreen} onBack={goBack} onChatHandoff={openChatWith} />;
        case 'notifications':   return <NotificationsScreen onNavigate={setCurrentScreen} onBack={goBack} />;
        case 'cards':           return <CardsScreen onNavigate={setCurrentScreen} onBack={goBack} />;
        case 'subscriptions':   return <SubscriptionsScreen onNavigate={setCurrentScreen} onBack={goBack} />;
        case 'settings':        return <SettingsScreen onNavigate={setCurrentScreen} onBack={goBack} />;
        case 'help':            return <HelpScreen onNavigate={setCurrentScreen} onBack={goBack} />;
        case 'travelNotice':    return <TravelNoticeScreen onNavigate={setCurrentScreen} onBack={goBack} />;
        case 'statements':      return <StatementsScreen onNavigate={setCurrentScreen} onBack={goBack} />;
        case 'rewards':         return <RewardsScreen onNavigate={setCurrentScreen} onBack={goBack} />;
        case 'flightSearch':    return <FlightSearchScreen onNavigate={setCurrentScreen} onBack={goBack} />;
        case 'payees':          return <PayeesScreen onNavigate={setCurrentScreen} onBack={goBack} />;
        case 'mortgageDetail':  return <MortgageDetailScreen onNavigate={setCurrentScreen} onBack={goBack} onChatHandoff={openChatWith} />;
        case 'testScenarios':   return <TestScenariosScreen onNavigate={setCurrentScreen} onBack={goBack} onChatHandoff={openChatWith} />;
        case 'changePassword':  return <ChangePasswordScreen currentUser={currentUser} forced={mustChangePw} loading={changePwLoading} error={changePwError} done={changePwDone} onSubmit={handleChangePassword} onBack={closeChangePw} />;
        case 'transactionDetail': return <TransactionDetailScreen onNavigate={handleNavigate} onBack={goBack} transaction={selectedTransaction} onChatHandoff={openChatWith} />;
        case 'investing':      return <InvestingScreen onChatHandoff={openChatWith} onNavigate={setCurrentScreen} onBack={goBack} />;
        case 'advisor':       return <AdvisorScreen onNavigate={handleNavigate} onBack={goBack} />;
        case 'bookAppointment': return <BookAppointmentScreen onNavigate={handleNavigate} onBack={goBack} requestContext={advisorRequest} onChatHandoff={openChatWith} />;
        case 'swipe':           return <SwipeScreen onNavigate={handleNavigate} onBack={goBack} />;
        case 'demoState':       return <DemoStateScreen onBack={goBack} customerId={sessionSummary?.customerId} chatHistoryLength={Array.isArray(activeChatHistory) ? activeChatHistory.length : 0} onClearChat={() => setActiveChatHistory([])} />;
        case 'upcomingFeatures': return <UpcomingFeaturesScreen onBack={goBack} />;
        default:               return <HomeScreen onNavigate={handleNavigate} onBack={goBack} />;
      }
    };
  const appContextValue = { selectedBackend, setSelectedBackend, selectedModel, setSelectedModel, ollamaNoThink, setOllamaNoThink, omlxNoThink, setOmlxNoThink, callState, setCallState, isCallStatusEnabled, setCallStatusEnabled, ttsEngine, setTtsEngine, elVoiceId, setElVoiceId, openaiVoiceId, setOpenaiVoiceId, qwenVoiceId, setQwenVoiceId, voiceOut, setVoiceOut, theme, setTheme, hideBalances, setHideBalances, voiceLogVisible, setVoiceLogVisible, composedSystemPrompt, promptSystemPrompt, setPromptSystemPrompt, promptScope, setPromptScope, promptGuidelines, setPromptGuidelines, promptSafety, setPromptSafety, promptOutputFormat, setPromptOutputFormat, promptEscalation, setPromptEscalation, promptFollowUp, setPromptFollowUp, summarizePrompt, setSummarizePrompt, currentUser, summarizeProvider, setSummarizeProvider, sessionId, sessionSummary, loadProfileSession, memoryVersion, isAdmin, openChangePassword: () => setCurrentScreen('changePassword'), simDate, setSimDate };
  const profileContextValue = { loadedProfileData, setLoadedProfileData };

  if (loginRequired && !isLoggedIn) {
    if (authMode === 'reset') {
      return (
        <div className="login-page">
          <div className="login-box">
            <ResetRequestForm
              loading={loginLoading}
              error={loginError}
              done={resetDone}
              onSubmit={handleRequestReset}
              onBackToSignIn={() => { setResetDone(false); setLoginError(null); setAuthNotice(null); setAuthMode('login'); }}
            />
          </div>
        </div>
      );
    }
    return (
      <div className="login-page">
        <div className="login-box">
          <div className="login-brand">TD</div>
          <h1>{authMode === 'register' ? 'Create your account' : 'Welcome back'}</h1>
          <p className="login-subtitle">
            {authMode === 'register' ? 'Register for access — an administrator will approve you.'
              : 'Sign in to keep going.'}
          </p>
          <form onSubmit={authMode === 'register' ? handleRegister : handleLogin} noValidate>
            <label htmlFor="username">Username</label>
            <input className="bh-input" type="text" id="username" name="username" autoComplete="username" placeholder="Your username" />
            <label htmlFor="password">Password</label>
            <input className="bh-input" type="password" id="password" name="password"
              autoComplete={authMode === 'register' ? 'new-password' : 'current-password'} placeholder="Your password" />
            {authNotice && <div className="login-notice" role="status">{authNotice}</div>}
            {loginError && <div className="login-error" role="alert">{loginError}</div>}
            <button className="bh-btn-primary login-submit" type="submit" disabled={loginLoading}>
              {loginLoading
                ? (authMode === 'register' ? 'Creating account…' : 'Signing you in…')
                : (authMode === 'register' ? 'Create account' : 'Sign in')}
            </button>
          </form>
          <div className="login-toggle-note">
            {authMode === 'register' ? (
              <>Already have an account?{' '}
                <button type="button" className="login-link-btn"
                  onClick={() => { setAuthMode('login'); setLoginError(null); setAuthNotice(null); }}>Sign in</button>
              </>
            ) : (
              <>
                <button type="button" className="login-link-btn"
                  onClick={() => { setAuthMode('reset'); setLoginError(null); setAuthNotice(null); }}>Forgot password?</button>
                <span style={{ margin: '0 8px', opacity: 0.4 }}>·</span>
                Need access?{' '}
                <button type="button" className="login-link-btn"
                  onClick={() => { setAuthMode('register'); setLoginError(null); setAuthNotice(null); }}>Register</button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <AppContext.Provider value={appContextValue}>
    <ProfileContext.Provider value={profileContextValue}>
    <div className="App" style={themes[theme]} data-theme={theme}>
      <div className="app-container">
        <div className="phone-viewport" ref={phoneViewportRef}>
          {simDate && SCREEN_TAB_MAP[currentScreen] && SCREEN_TAB_MAP[currentScreen] !== 'settings' && (
            <div style={{position:'absolute', top:0, left:0, right:0, zIndex:2000, background:'rgba(47,191,0,0.12)', borderBottom:'1px solid rgba(47,191,0,0.35)', padding:'3px 10px', display:'flex', alignItems:'center', justifyContent:'space-between', pointerEvents:'none', backdropFilter:'blur(4px)'}}>
              <span style={{fontSize:9, fontFamily:'monospace', color:'#2FBF00', letterSpacing:0.8, fontWeight:700}}>▶ SIM {simDate}</span>
              <span style={{fontSize:9, fontFamily:'monospace', color:'rgba(47,191,0,0.6)', letterSpacing:0.5}}>home · budget responding</span>
            </div>
          )}
          <main role="main" aria-label={currentScreen} style={{display:'contents'}}>
            {renderScreen()}
          </main>
          <BottomNav currentScreen={currentScreen} onNavigate={setCurrentScreen} onBack={goBack} busyTab={agentsRunning ? 'insights' : null} />

          {/* Floating chat button + modal — always in DOM to avoid re-mount flash */}
          <button
            className="chat-fab"
            style={{ display: isChatOpen ? 'none' : undefined }}
            onClick={() => {
              setChatEverToggled(true);
              setIsChatOpen(true);
            }}
            aria-label="Open chat"
          >
            <span className="chat-fab-icon">
              <MdChat size={24} />
            </span>
            <span className="chat-fab-pulse" />
          </button>

          {/* Chat modal overlay */}
          <div
            className={[
              'chat-modal-overlay',
              chatEverToggled ? 'chat-modal--ready' : '',
              (chatEverToggled && (isChatOpen || currentScreen !== 'voiceBot')) ? 'chat-modal--shown' : '',
              isChatOpen ? 'chat-modal--open' : '',
            ].join(' ')}>
            <div className="chat-modal-drag-handle" onClick={() => setIsChatOpen(false)}>
              <div className="chat-modal-handle-bar" />
            </div>
            {chatEverToggled && (
              <ChatScreen
                isModal
                onClose={() => setIsChatOpen(false)}
                onNavigate={(screen, data) => { setIsChatOpen(false); handleNavigate(screen, data); }}
                onSaveToDashboard={handlePinMessage}
                pinnedMessageIds={savedDashboardItems.map(i => i.id)}
                onSaveSession={handleSaveSession}
                sessionToLoad={sessionToLoad}
                activeHistory={activeChatHistory}
                onHistoryChange={setActiveChatHistory}
                currentScreenContext={currentScreen}
                onLog={addVoiceLog}
              />
            )}
          </div>


        </div>
        {/* Top-right toolbar — console, screenshot, theme grouped in one row so the
            icons never overlap the pill column (profile / tests / memory) below. */}
        {!isMobile && (
          <div style={{ position: 'fixed', top: 14, right: 14, zIndex: 1201, display: 'flex', alignItems: 'center', gap: 8 }}>
            {/* Console toggle */}
            <button
              className="global-save-btn"
              onClick={() => setVoiceLogVisible(v => !v)}
              title={voiceLogVisible ? 'Hide console' : 'Show console'}
              aria-pressed={!!voiceLogVisible}
              style={{
                position: 'static', top: 'auto', right: 'auto',
                background: voiceLogVisible ? 'rgba(74,158,255,0.25)' : undefined,
                borderColor: voiceLogVisible ? 'rgba(74,158,255,0.5)' : undefined,
                color: voiceLogVisible ? '#4a9eff' : undefined,
              }}
            >
              <MdTerminal size={14} />
            </button>
            {/* Screenshot current screen */}
            <button
              className={`global-save-btn${globalSaveStatus ? ` ${globalSaveStatus}` : ''}`}
              onClick={handleGlobalSave}
              disabled={globalSaveStatus === 'saving'}
              title="Screenshot current screen"
              style={{ position: 'static', top: 'auto', right: 'auto' }}
            >
              <MdCameraAlt size={14} />
            </button>
            {/* Theme toggle */}
            <div className="theme-toggle-float" style={{ position: 'static', top: 'auto', right: 'auto' }}>
              <button className={`theme-toggle-btn${theme === 'green' ? ' active' : ''}`} title="Light" onClick={() => setTheme('green')}>☀️</button>
              <button className={`theme-toggle-btn${theme === 'purple' ? ' active' : ''}`} title="Dark" onClick={() => setTheme('purple')}>🌙</button>
            </div>
          </div>
        )}

        {/* Profile picker — top right, below theme toggle (desktop only) */}
        {!isMobile && (
          <div style={{ position: 'fixed', top: 52, right: 14, zIndex: 1200 }}>
            <div className={`profile-picker-float${loadedProfileData ? ' loaded' : ''}`}
              onClick={() => setProfileDropdownOpen(p => p === 'float' ? null : 'float')}
              style={{ cursor: 'pointer', userSelect: 'none' }}>
              <MdPerson size={14} />
              <span>{loadedProfileData ? loadedProfileData.name.split(' ')[0] : 'Profile'}</span>
            </div>
            {profileDropdownOpen === 'float' && (
              <>
                <div style={{ position: 'fixed', inset: 0, zIndex: -1 }} onClick={() => setProfileDropdownOpen(null)} />
                <div className="profile-dropdown">
                  {PROFILE_OPTIONS.map(opt => (
                    <button key={opt.key} className={`profile-dropdown-item${loadedProfileData?.source === opt.key || (opt.key === 'none' && !loadedProfileData) ? ' active' : ''}`}
                      onClick={() => loadProfile(opt.key)}>
                      <span className="profile-dropdown-label">{opt.label}</span>
                      <span className="profile-dropdown-sub">{opt.sub}</span>
                    </button>
                  ))}
                  {loadedProfileData?.source === 'csv-upload' && (
                    <button className="profile-dropdown-item active" onClick={() => { setProfileDropdownOpen(null); csvFileRef.current?.click(); }}>
                      <span className="profile-dropdown-label"><MdUpload size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />{loadedProfileData.name}</span>
                      <span className="profile-dropdown-sub">CSV loaded — click to replace</span>
                    </button>
                  )}
                  <button className="profile-dropdown-item" onClick={() => { setProfileDropdownOpen(null); csvFileRef.current?.click(); }}
                    style={{ borderTop: '1px dashed var(--border-color, #e5e7eb)', marginTop: 4 }}>
                    <span className="profile-dropdown-label"><MdUpload size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />Upload CSV…</span>
                    <span className="profile-dropdown-sub">Temp profile from transactions</span>
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Test scenarios + memory reset — desktop only, side-by-side in one row at
            top:90 so neither pill drops to top:128 where it would overlap the console
            panel (which starts at top:130). Memory clears IVA's learned-memory file
            (concepts, corrections, prefs); the server re-bootstraps from transactions. */}
        {!isMobile && (hasGroundTruth(loadedProfileData?.source) || sessionSummary?.customerId) && (
          <div style={{ position: 'fixed', top: 90, right: 14, zIndex: 1200, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            {sessionSummary?.customerId && (
              <div className="profile-picker-float loaded"
                onClick={handleClearMemory}
                style={{ cursor: 'pointer', userSelect: 'none' }}
                title="Clear IVA's learned customer memory (concepts, corrections, preferences)">
                <MdDeleteSweep size={14} />
                <span>{memoryClearStatus === 'done' ? 'Cleared ✓' : memoryClearStatus === 'error' ? 'Failed' : 'Memory'}</span>
              </div>
            )}
            {sessionSummary?.customerId && (
              <div className="profile-picker-float loaded"
                onClick={() => setCurrentScreen('scenario')}
                style={{ cursor: 'pointer', userSelect: 'none' }}
                title="Scenario Runner — replay transactions over time">
                <MdPlayArrow size={14} />
                <span>Scenario</span>
              </div>
            )}
            {hasGroundTruth(loadedProfileData?.source) && (
              <div className="profile-picker-float loaded"
                onClick={() => setCurrentScreen('testScenarios')}
                style={{ cursor: 'pointer', userSelect: 'none' }}
                title="Ground-truth test scenarios for this persona">
                <MdScience size={14} />
                <span>Tests</span>
              </div>
            )}
          </div>
        )}


        {/* Demo Nav — liquid glass */}
        <div className="demo-nav">
          <div className="demo-nav-build-info">v{version} · {buildDate.slice(0, 16)}</div>
          <p className="demo-nav-title">Demo Nav</p>
          <div className="demo-nav-login-toggle">
            <RoleBadge isAdmin={isAdmin} isLoggedIn={isLoggedIn} loginRequired={loginRequired} />
            <span>Login required:</span>
            <button className={`demo-nav-login-btn${loginRequired ? ' active' : ''}`} onClick={() => handleToggleLoginRequired(!loginRequired)}>
              {loginRequired ? 'ON' : 'OFF'}
            </button>
            {loginRequired && isLoggedIn && (
              <button className="demo-nav-login-btn" onClick={handleLogout}>Logout</button>
            )}
          </div>

          {/* Screens */}
          <div className="demo-nav-grid">
            {[
              ['home','Home'],['accounts','Accts'],['search','Search'],['credit','Credit'],
              ['cardMgmt','Card Mgmt'],['cards','Cards'],['receipt','Receipt'],['depositDetail','Deposit'],
              ['security','Security'],['call','Call'],['callVerify','Verify'],
              ['services','Services'],['chatHistory','History'],['dashboard','Dashboard'],
              ['portfolio','Portfolio'],['goalsScreen','Goals'],['insightsScreen','Insights'],['agents','Agents'],['finances','Finances'],['spendingAnalysis','Spend+'],
              ['spendingScreen','Trends'],['locations','Locations'],['financialSummary','Fin Sum'],
              ['budgetRule','70/20/10'],['budgetScreen','Budget'],['scenario','Scenario'],['profileScreen','Profile'],
              ['emailTransfer','e-Transfer'],['billPayment','Bill Pay'],
              ['voiceBot','Voice'],['notifications','Notif'],
              ['mortgages','Mortgages'],['openAccount','Open Acct'],['todaysRates','Rates'],['subscriptions','Subs'],
              ['settings','Settings'],['help','Help'],['travelNotice','Travel'],
              ['statements','Stmts'],['rewards','Rewards'],['payees','Payees'],
              ['flightSearch','Flights'],['mortgageDetail','Mortgage'],['swipe','Swipe'],
            ].map(([screen, label]) => (
              <button key={screen} className={`demo-nav-btn${currentScreen === screen ? ' active' : ''}`} onClick={() => setCurrentScreen(screen)}>{label}</button>
            ))}
          </div>

          <button className="demo-nav-full-btn" onClick={() => setIsChatOpen(p => !p)}
            style={{ background: isChatOpen ? 'rgba(96,85,247,0.75)' : 'rgba(56,142,60,0.75)', color: 'white', marginTop: 4 }}>
            <MdChat size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            {isChatOpen ? 'Close Chat' : 'Open Chat'}
          </button>

          <button className={`demo-nav-full-btn${globalSaveStatus ? ` global-save-${globalSaveStatus}` : ''}`}
            onClick={handleGlobalSave} disabled={globalSaveStatus === 'saving'}
            style={{ background: globalSaveStatus === 'saved' ? 'rgba(0,180,80,0.7)' : globalSaveStatus === 'error' ? 'rgba(220,50,50,0.7)' : 'rgba(60,60,80,0.75)', color: 'white', marginTop: 4 }}>
            <MdCameraAlt size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            {globalSaveStatus === 'saving' ? 'Saving...' : globalSaveStatus === 'saved' ? 'Saved!' : 'Screenshot'}
          </button>

          <hr className="demo-nav-hr" />

          {/* Voice */}
          <p className="demo-nav-label">Voice</p>
          <div className="demo-nav-grid" style={{ marginBottom: 4 }}>
            {[['browser', <MdVolumeUp size={12} />, 'Browser'], ['elevenlabs', <SiElevenlabs size={12} />, 'ElevenLabs'], ['qwen', <MdRecordVoiceOver size={12} />, 'Qwen']].map(([val, icon, label]) => (
              <button key={val} className={`demo-nav-btn${ttsEngine === val ? ' active' : ''}`} onClick={() => setTtsEngine(val)} style={{ display: 'flex', alignItems: 'center', gap: 3, justifyContent: 'center' }}>
                {icon}{label}
              </button>
            ))}
          </div>
          {ttsEngine === 'elevenlabs' && (
            <select className="demo-nav-select" value={elVoiceId} onChange={e => setElVoiceId(e.target.value)}>
              {ELEVENLABS_VOICES.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
            </select>
          )}
          {ttsEngine === 'qwen' && (
            <select value={qwenVoiceId} onChange={e => setQwenVoiceId(e.target.value)} className="demo-nav-input" aria-label="Qwen voice" style={{ width: 110, fontSize: 11 }}>
              {['aiden','dylan','eric','ono_anna','ryan','serena','sohee','uncle_fu','vivian'].map(v => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          )}
          <button className="demo-nav-full-btn" onClick={() => setVoiceOut(v => !v)}
            style={{ background: voiceOut ? 'rgba(56,142,60,0.75)' : 'rgba(96,96,96,0.6)', color: 'white', marginTop: 4 }}>
            {voiceOut ? <MdVolumeUp size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} /> : <MdVolumeOff size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />}
            {voiceOut ? 'Voice On' : 'Voice Off'}
          </button>
          <p className="demo-nav-label" style={{ marginTop: 6, marginBottom: 2 }}>Voice Summary Backend</p>
          <select className="demo-nav-select" value={summarizeProvider} onChange={e => setSummarizeProvider(e.target.value)}>
            <option value="auto">Auto — cheapest available</option>
            <option value="same">Use selected AI backend</option>
            <optgroup label="── Free ──────────────────────">
              <option value="groq">Groq — Llama 3.1 8B Instant</option>
              <option value="gemini">Gemini — Flash-Lite</option>
              <option value="ollama">Ollama — local model</option>
              <option value="omlx">oMLX — local model</option>
              <option value="openrouter">OpenRouter — Nemotron Nano 9B</option>
            </optgroup>
            <optgroup label="── Paid ──────────────────────">
              <option value="cohere">Cohere — Command R</option>
              <option value="openai">OpenAI — GPT-4o-mini</option>
              <option value="claude">Claude — Haiku 4.5</option>
            </optgroup>
          </select>

          <hr className="demo-nav-hr" />

          {/* AI Backend — grouped by tier (Free / Paid / Local), inline */}
          <p className="demo-nav-label">AI Backend</p>
          <div className="demo-nav-tier-row">
            <span className="demo-nav-sublabel">Free</span>
            <div className="demo-nav-grid-2">
              {[['cohere','Cohere'],['openrouter','OpenRouter'],['gemini','Gemini'],['groq','Groq']].map(([val, label]) => (
                <button key={val} className={`demo-nav-btn${selectedBackend === val ? ' active' : ''}`} onClick={() => setSelectedBackend(val)}>{label}</button>
              ))}
            </div>
          </div>
          <div className="demo-nav-tier-row">
            <span className="demo-nav-sublabel">Paid</span>
            <div className="demo-nav-grid-2">
              {[['claude','Claude'],['openai','OpenAI']].map(([val, label]) => (
                <button key={val} className={`demo-nav-btn${selectedBackend === val ? ' active' : ''}`} onClick={() => setSelectedBackend(val)}>{label}</button>
              ))}
            </div>
          </div>
          <div className="demo-nav-tier-row">
            <span className="demo-nav-sublabel">Local</span>
            <div className="demo-nav-grid-2">
              {[['ollama','Ollama'],['omlx','oMLX']].map(([val, label]) => (
                <button key={val} className={`demo-nav-btn${selectedBackend === val ? ' active' : ''}`} onClick={() => setSelectedBackend(val)}>{label}</button>
              ))}
            </div>
          </div>

          <hr className="demo-nav-hr" />

          {/* Model selector */}
          <div className="demo-nav-row">
            <p className="demo-nav-label" style={{margin: 0, whiteSpace: 'nowrap'}}>Model</p>
            <select className="demo-nav-select" value={selectedModel} onChange={e => setSelectedModel(e.target.value)}>
              {renderModelOptions(selectedBackend)}
            </select>
          </div>

          {selectedBackend === 'ollama' && (
            <button className={`demo-nav-full-btn${ollamaNoThink ? ' active' : ''}`}
              onClick={() => setOllamaNoThink(v => !v)}
              style={{ background: ollamaNoThink ? 'rgba(255,149,0,0.75)' : 'rgba(96,96,96,0.6)', color: 'white', marginTop: 4 }}>
              {ollamaNoThink ? '⚡ No Think (on)' : '🧠 Think (on)'}
            </button>
          )}

          {selectedBackend === 'omlx' && (
            <button className={`demo-nav-full-btn${omlxNoThink ? ' active' : ''}`}
              onClick={() => setOmlxNoThink(v => !v)}
              style={{ background: omlxNoThink ? 'rgba(255,149,0,0.75)' : 'rgba(96,96,96,0.6)', color: 'white', marginTop: 4 }}>
              {omlxNoThink ? '⚡ No Think (on)' : '🧠 Think (on)'}
            </button>
          )}

          <hr className="demo-nav-hr" />

          {/* Call State */}
          <p className="demo-nav-label">Call State</p>
          <div className="demo-nav-grid" style={{marginBottom: 4}}>
            {[['none','None'],['active','Active'],['recent','Recent']].map(([val, label]) => (
              <button key={val} className={`demo-nav-btn${callState === val ? ' active' : ''}`}
                onClick={() => { setCallState(val); setCallStatusEnabled(val !== 'none'); }}>{label}</button>
            ))}
          </div>
          <button className="demo-nav-full-btn"
            onClick={() => setCallStatusEnabled(!isCallStatusEnabled)}
            style={{background: isCallStatusEnabled ? 'rgba(211,47,47,0.75)' : 'rgba(56,142,60,0.75)', color: 'white'}}>
            {isCallStatusEnabled ? 'Hide Banner' : 'Show Banner'}
          </button>

          <hr className="demo-nav-hr" />

          {/* Profile */}
          <div style={{ position: 'relative' }}>
            <button className="demo-nav-full-btn" onClick={() => setProfileDropdownOpen(p => p === 'demo' ? null : 'demo')}
              style={{background: loadedProfileData ? 'rgba(21,101,192,0.8)' : 'rgba(96,125,139,0.6)', color: 'white', width: '100%'}}>
              <MdPerson size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />{loadedProfileData ? loadedProfileData.name : 'No Profile'}
            </button>
            {profileDropdownOpen === 'demo' && (
              <>
                <div style={{ position: 'fixed', inset: 0, zIndex: 1299 }} onClick={() => setProfileDropdownOpen(null)} />
                <div className="profile-dropdown" style={{ bottom: '100%', top: 'auto', marginBottom: 6, zIndex: 1300 }}>
                  {PROFILE_OPTIONS.map(opt => (
                    <button key={opt.key} className={`profile-dropdown-item${loadedProfileData?.source === opt.key || (opt.key === 'none' && !loadedProfileData) ? ' active' : ''}`}
                      onClick={() => loadProfile(opt.key)}>
                      <span className="profile-dropdown-label">{opt.label}</span>
                      <span className="profile-dropdown-sub">{opt.sub}</span>
                    </button>
                  ))}
                  {loadedProfileData?.source === 'csv-upload' && (
                    <button className="profile-dropdown-item active" onClick={() => { setProfileDropdownOpen(null); csvFileRef.current?.click(); }}>
                      <span className="profile-dropdown-label"><MdUpload size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />{loadedProfileData.name}</span>
                      <span className="profile-dropdown-sub">CSV loaded — click to replace</span>
                    </button>
                  )}
                  <button className="profile-dropdown-item" onClick={() => { setProfileDropdownOpen(null); csvFileRef.current?.click(); }}
                    style={{ borderTop: '1px dashed var(--border-color, #e5e7eb)', marginTop: 4 }}>
                    <span className="profile-dropdown-label"><MdUpload size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />Upload CSV…</span>
                    <span className="profile-dropdown-sub">Temp profile from transactions</span>
                  </button>
                </div>
              </>
            )}
          </div>

          <hr className="demo-nav-hr" />

          {/* Demo State — reset + memory viewer */}
          <button className="demo-nav-full-btn"
            onClick={() => setCurrentScreen('demoState')}
            style={{ background: currentScreen === 'demoState' ? 'rgba(211,47,47,0.85)' : 'rgba(180,40,40,0.65)', color: 'white' }}>
            Demo State →
          </button>

          {/* Upcoming Features — admin toggle + nav button */}
          {isAdmin && (
            <>
              <hr className="demo-nav-hr" />
              <p className="demo-nav-label">Testers</p>
              <button className="demo-nav-full-btn"
                onClick={() => setShowUpcoming(v => !v)}
                style={{ background: showUpcoming ? 'rgba(56,142,60,0.75)' : 'rgba(96,96,96,0.6)', color: 'white' }}>
                {showUpcoming ? '✓ Upcoming features visible' : 'Upcoming features hidden'}
              </button>
            </>
          )}
          {(showUpcoming || isAdmin) && (
            <button className="demo-nav-full-btn"
              onClick={() => setCurrentScreen('upcomingFeatures')}
              style={{ background: 'rgba(81,45,168,0.75)', color: 'white', marginTop: 4 }}>
              What's next →
            </button>
          )}
        </div>

        {/* Mobile: top-center pulldown trigger tab — web only */}
        {isMobile && (
          <button
            className={`demo-pulldown-tab${demoTabCollapsed ? ' collapsed' : ''}`}
            onPointerDown={onDemoTabPointerDown}
            onPointerMove={onDemoTabPointerMove}
            onPointerUp={onDemoTabPointerUp}
            style={demoTabX != null ? { left: `${demoTabX}px`, transform: 'none' } : undefined}
            aria-label="Demo controls — tap to open, double-tap to shrink, drag to move"
            title="Tap: open · Double-tap: shrink · Drag: move"
          >
            {demoTabCollapsed ? '' : (demoDrawerOpen ? '▲ DEV' : '▼ DEV')}
          </button>
        )}

        {/* Mobile: drawer overlay + panel — web only */}
        {demoDrawerOpen && (
          <div className="demo-drawer-overlay" onClick={() => setDemoDrawerOpen(false)} />
        )}
        {(
          <div className={`demo-drawer${demoDrawerOpen ? ' open' : ''}`}>
            <div className="demo-drawer-header">
              <span className="demo-nav-title" style={{ margin: 0 }}>Demo Controls</span>
              <button className="demo-drawer-close" onClick={() => setDemoDrawerOpen(false)}>✕</button>
            </div>

            {/* Screens */}
            <div className="demo-nav-grid">
              {[
                ['home','Home'],['accounts','Accts'],['search','Search'],['credit','Credit'],
                ['cardMgmt','Card Mgmt'],['cards','Cards'],['receipt','Receipt'],['depositDetail','Deposit'],
                ['security','Security'],['call','Call'],['callVerify','Verify'],
                ['services','Services'],['chatHistory','History'],['dashboard','Dashboard'],
                ['portfolio','Portfolio'],['goalsScreen','Goals'],['insightsScreen','Insights'],['agents','Agents'],['finances','Finances'],['spendingAnalysis','Spend+'],
                ['spendingScreen','Trends'],['locations','Locations'],['financialSummary','Fin Sum'],
                ['budgetRule','70/20/10'],['budgetScreen','Budget'],['scenario','Scenario'],['profileScreen','Profile'],
                ['emailTransfer','e-Transfer'],['billPayment','Bill Pay'],
                ['voiceBot','Voice'],['notifications','Notif'],
                ['mortgages','Mortgages'],['openAccount','Open Acct'],['todaysRates','Rates'],['subscriptions','Subs'],
                ['settings','Settings'],['help','Help'],['travelNotice','Travel'],
                ['statements','Stmts'],['rewards','Rewards'],['payees','Payees'],
                ['flightSearch','Flights'],['mortgageDetail','Mortgage'],['swipe','Swipe'],
                ...((showUpcoming || isAdmin) ? [['upcomingFeatures', "What's Next"]] : []),
              ].map(([screen, label]) => (
                <button key={screen} className={`demo-nav-btn${currentScreen === screen ? ' active' : ''}`}
                  onClick={() => { setCurrentScreen(screen); setDemoDrawerOpen(false); }}>{label}</button>
              ))}
            </div>

            <button className="demo-nav-full-btn" onClick={() => { setIsChatOpen(p => !p); setDemoDrawerOpen(false); }}
              style={{ background: isChatOpen ? 'rgba(96,85,247,0.75)' : 'rgba(56,142,60,0.75)', color: 'white', marginTop: 4 }}>
              <MdChat size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />
              {isChatOpen ? 'Close Chat' : 'Open Chat'}
            </button>

            <button className={`demo-nav-full-btn${globalSaveStatus ? ` global-save-${globalSaveStatus}` : ''}`}
              onClick={async () => { await handleGlobalSave(); }}
              disabled={globalSaveStatus === 'saving'}
              style={{ background: globalSaveStatus === 'saved' ? 'rgba(0,180,80,0.7)' : globalSaveStatus === 'error' ? 'rgba(220,50,50,0.7)' : 'rgba(60,60,80,0.75)', color: 'white', marginTop: 4 }}>
              <MdCameraAlt size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />
              {globalSaveStatus === 'saving' ? 'Saving...' : globalSaveStatus === 'saved' ? 'Saved!' : 'Screenshot'}
            </button>

            <hr className="demo-nav-hr" />

            {/* Voice */}
            <p className="demo-nav-label">Voice</p>
            <div className="demo-nav-grid" style={{ marginBottom: 4 }}>
              {[['browser', <MdVolumeUp size={12} />, 'Browser'], ['elevenlabs', <SiElevenlabs size={12} />, 'ElevenLabs'], ['qwen', <MdRecordVoiceOver size={12} />, 'Qwen']].map(([val, icon, label]) => (
                <button key={val} className={`demo-nav-btn${ttsEngine === val ? ' active' : ''}`} onClick={() => setTtsEngine(val)} style={{ display: 'flex', alignItems: 'center', gap: 3, justifyContent: 'center' }}>
                  {icon}{label}
                </button>
              ))}
            </div>
            {ttsEngine === 'elevenlabs' && (
              <select className="demo-nav-select" value={elVoiceId} onChange={e => setElVoiceId(e.target.value)}>
                {ELEVENLABS_VOICES.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
              </select>
            )}
            {ttsEngine === 'qwen' && (
              <select value={qwenVoiceId} onChange={e => setQwenVoiceId(e.target.value)} className="demo-nav-input" aria-label="Qwen voice" style={{ width: 110, fontSize: 11 }}>
                {['aiden','dylan','eric','ono_anna','ryan','serena','sohee','uncle_fu','vivian'].map(v => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            )}
            <button className="demo-nav-full-btn" onClick={() => setVoiceOut(v => !v)}
              style={{ background: voiceOut ? 'rgba(56,142,60,0.75)' : 'rgba(96,96,96,0.6)', color: 'white', marginTop: 4 }}>
              {voiceOut ? <MdVolumeUp size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} /> : <MdVolumeOff size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />}
              {voiceOut ? 'Voice On' : 'Voice Off'}
            </button>
            <p className="demo-nav-label" style={{ marginTop: 6, marginBottom: 2 }}>Voice Summary Backend</p>
            <select className="demo-nav-select" value={summarizeProvider} onChange={e => setSummarizeProvider(e.target.value)}>
              <option value="auto">Auto — Ollama → oMLX → Groq → Gemini → OpenRouter</option>
              <option value="same">Use selected AI backend</option>
              <optgroup label="── Free ──────────────────────">
                <option value="groq">Groq — Llama 3.1 8B Instant</option>
                <option value="gemini">Gemini — Flash-Lite</option>
                <option value="ollama">Ollama — local model</option>
                <option value="omlx">oMLX — local model</option>
                <option value="openrouter">OpenRouter — Nemotron Nano 9B</option>
              </optgroup>
              <optgroup label="── Paid ──────────────────────">
                <option value="cohere">Cohere — Command R</option>
                <option value="openai">OpenAI — GPT-4o-mini</option>
                <option value="claude">Claude — Haiku 4.5</option>
              </optgroup>
            </select>

            <hr className="demo-nav-hr" />

            {/* AI Backend — grouped by tier (Free / Paid / Local), inline */}
            <p className="demo-nav-label">AI Backend</p>
            <div className="demo-nav-tier-row">
              <span className="demo-nav-sublabel">Free</span>
              <div className="demo-nav-grid-2">
                {[['cohere','Cohere'],['openrouter','OpenRouter']].map(([val, label]) => (
                  <button key={val} className={`demo-nav-btn${selectedBackend === val ? ' active' : ''}`} onClick={() => setSelectedBackend(val)}>{label}</button>
                ))}
              </div>
            </div>
            <div className="demo-nav-tier-row">
              <span className="demo-nav-sublabel">Paid</span>
              <div className="demo-nav-grid-2">
                {[['claude','Claude'],['openai','OpenAI']].map(([val, label]) => (
                  <button key={val} className={`demo-nav-btn${selectedBackend === val ? ' active' : ''}`} onClick={() => setSelectedBackend(val)}>{label}</button>
                ))}
              </div>
            </div>
            <div className="demo-nav-tier-row">
              <span className="demo-nav-sublabel">Local</span>
              <div className="demo-nav-grid-2">
                {[['ollama','Ollama'],['omlx','oMLX']].map(([val, label]) => (
                  <button key={val} className={`demo-nav-btn${selectedBackend === val ? ' active' : ''}`} onClick={() => setSelectedBackend(val)}>{label}</button>
                ))}
              </div>
            </div>

            <div className="demo-nav-row">
              <p className="demo-nav-label" style={{margin: 0, whiteSpace: 'nowrap'}}>Model</p>
              <select className="demo-nav-select" value={selectedModel} onChange={e => setSelectedModel(e.target.value)}>
                {renderModelOptions(selectedBackend)}
              </select>
            </div>

            {selectedBackend === 'ollama' && (
              <button className={`demo-nav-full-btn${ollamaNoThink ? ' active' : ''}`}
                onClick={() => setOllamaNoThink(v => !v)}
                style={{ background: ollamaNoThink ? 'rgba(255,149,0,0.75)' : 'rgba(96,96,96,0.6)', color: 'white', marginTop: 4 }}>
                {ollamaNoThink ? '⚡ No Think (on)' : '🧠 Think (on)'}
              </button>
            )}

            <hr className="demo-nav-hr" />

            {/* Profile */}
            <div style={{ position: 'relative' }}>
              <button className="demo-nav-full-btn" onClick={() => setProfileDropdownOpen(p => p === 'drawer' ? null : 'drawer')}
                style={{background: loadedProfileData ? 'rgba(21,101,192,0.8)' : 'rgba(96,125,139,0.6)', color: 'white', width: '100%'}}>
                <MdPerson size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />{loadedProfileData ? loadedProfileData.name : 'No Profile'}
              </button>
              {profileDropdownOpen === 'drawer' && (
                <>
                  <div style={{ position: 'fixed', inset: 0, zIndex: 1299 }} onClick={() => setProfileDropdownOpen(null)} />
                  <div className="profile-dropdown" style={{ bottom: '100%', top: 'auto', marginBottom: 6, zIndex: 1300 }}>
                    {PROFILE_OPTIONS.map(opt => (
                      <button key={opt.key} className={`profile-dropdown-item${loadedProfileData?.source === opt.key || (opt.key === 'none' && !loadedProfileData) ? ' active' : ''}`}
                        onClick={() => { loadProfile(opt.key); setProfileDropdownOpen(null); }}>
                        <span className="profile-dropdown-label">{opt.label}</span>
                        <span className="profile-dropdown-sub">{opt.sub}</span>
                      </button>
                    ))}
                    {loadedProfileData?.source === 'csv-upload' && (
                      <button className="profile-dropdown-item active" onClick={() => { setProfileDropdownOpen(null); csvFileRef.current?.click(); }}>
                        <span className="profile-dropdown-label"><MdUpload size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />{loadedProfileData.name}</span>
                        <span className="profile-dropdown-sub">CSV loaded — click to replace</span>
                      </button>
                    )}
                    <button className="profile-dropdown-item" onClick={() => { setProfileDropdownOpen(null); csvFileRef.current?.click(); }}
                      style={{ borderTop: '1px dashed var(--border-color, #e5e7eb)', marginTop: 4 }}>
                      <span className="profile-dropdown-label"><MdUpload size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />Upload CSV…</span>
                      <span className="profile-dropdown-sub">Temp profile from transactions</span>
                    </button>
                  </div>
                </>
              )}
            </div>

            <hr className="demo-nav-hr" />

            {/* Call State */}
            <p className="demo-nav-label">Call State</p>
            <div className="demo-nav-grid" style={{ marginBottom: 4 }}>
              {[['none','None'],['active','Active'],['recent','Recent']].map(([val, label]) => (
                <button key={val} className={`demo-nav-btn${callState === val ? ' active' : ''}`}
                  onClick={() => { setCallState(val); setCallStatusEnabled(val !== 'none'); }}>{label}</button>
              ))}
            </div>
            <button className="demo-nav-full-btn"
              onClick={() => setCallStatusEnabled(!isCallStatusEnabled)}
              style={{ background: isCallStatusEnabled ? 'rgba(211,47,47,0.75)' : 'rgba(56,142,60,0.75)', color: 'white' }}>
              {isCallStatusEnabled ? 'Hide Banner' : 'Show Banner'}
            </button>

            <hr className="demo-nav-hr" />

            {/* Demo State — reset + memory viewer */}
            <button className="demo-nav-full-btn"
              onClick={() => { setCurrentScreen('demoState'); setDemoDrawerOpen(false); }}
              style={{ background: currentScreen === 'demoState' ? 'rgba(211,47,47,0.85)' : 'rgba(180,40,40,0.65)', color: 'white' }}>
              Demo State →
            </button>

            {/* Upcoming Features — admin toggle + nav button */}
            {isAdmin && (
              <>
                <hr className="demo-nav-hr" />
                <p className="demo-nav-label">Testers</p>
                <button className="demo-nav-full-btn"
                  onClick={() => setShowUpcoming(v => !v)}
                  style={{ background: showUpcoming ? 'rgba(56,142,60,0.75)' : 'rgba(96,96,96,0.6)', color: 'white' }}>
                  {showUpcoming ? '✓ Upcoming features visible' : 'Upcoming features hidden'}
                </button>
              </>
            )}
            {(showUpcoming || isAdmin) && (
              <button className="demo-nav-full-btn"
                onClick={() => { setCurrentScreen('upcomingFeatures'); setDemoDrawerOpen(false); }}
                style={{ background: 'rgba(81,45,168,0.75)', color: 'white', marginTop: 4 }}>
                What's next →
              </button>
            )}
          </div>
        )}

        {/* Profile Modal */}
        {showProfileModal && (() => {
          const p = loadedProfileData?.profile;
          const customer = p?.customer || {};
          const accounts = p?.accounts || {};
          const name = customer.first_name && customer.last_name
            ? `${customer.first_name} ${customer.last_name}`
            : loadedProfileData?.name || 'Unknown';
          const fmtCad = v => v != null ? `$${Math.abs(Number(v)).toLocaleString('en-CA', {minimumFractionDigits: 2, maximumFractionDigits: 2})}` : '—';
          const acctBal = (a) => a?.current_balance ?? a?.current_balance_owing ?? a?.current_value ?? a?.current_market_value ?? a?.balance;
          const chequingBal = acctBal(accounts.chequing);
          const savingsBal  = acctBal(accounts.savings);
          const creditBal   = acctBal(accounts.credit_card);
          const investBal   = acctBal(accounts.investing);
          const netWorth = customer.net_worth != null ? customer.net_worth : (customer.total_assets_cad != null ? (customer.total_assets_cad - (customer.total_liabilities || 0)) : null);
          const goals = p?.goals || loadedProfileData?.goals || [];
          const budgetStats = p ? computeBudgetRule(p) : null;
          const avgIncome = budgetStats?.avgIncome ?? null;
          const avgExpenses = budgetStats ? (budgetStats.avgNeeds + budgetStats.avgDisc + budgetStats.avgDebt) : null;
          return (
            <div style={{position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center'}} onClick={() => setShowProfileModal(false)}>
              <div style={{background:'#fff', borderRadius:12, padding:'20px 22px', width:320, maxHeight:'85vh', overflowY:'auto', boxShadow:'0 8px 32px rgba(0,0,0,0.28)', position:'relative'}} onClick={e => e.stopPropagation()}>
                {/* Close */}
                <button onClick={() => setShowProfileModal(false)} style={{position:'absolute', top:10, right:12, background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#555', lineHeight:1}}>×</button>

                {!loadedProfileData ? (
                  <div style={{textAlign:'center', padding:'24px 0', color:'#666'}}>
                    <MdPerson size={36} style={{ color: '#999', marginBottom: 8 }} />
                    <div style={{fontWeight:'bold', marginBottom:4}}>No Profile Loaded</div>
                    <div style={{fontSize:12}}>Upload a profile in the Chat screen to see financial data here.</div>
                  </div>
                ) : (
                  <>
                    {/* Header */}
                    <div style={{marginBottom:16, paddingBottom:12, borderBottom:'1px solid #eee'}}>
                      <div style={{fontSize:11, color:'#888', textTransform:'uppercase', letterSpacing:1, marginBottom:2}}>{loadedProfileData.source} Profile</div>
                      <div style={{fontSize:18, fontWeight:'bold', color:'#111'}}>{name}</div>
                      {(customer.age || customer.employment?.occupation || customer.address?.city) && (
                        <div style={{fontSize:12, color:'#555', marginTop:3}}>
                          {[customer.age && `Age ${customer.age}`, customer.employment?.occupation, customer.address?.city && customer.address?.province ? `${customer.address.city}, ${customer.address.province}` : customer.address?.city].filter(Boolean).join(' · ')}
                        </div>
                      )}
                      {customer.employment?.employer && (
                        <div style={{fontSize:11, color:'#888', marginTop:2}}>{customer.employment.employer}</div>
                      )}
                    </div>

                    {/* Account Balances */}
                    <div style={{marginBottom:14}}>
                      <div style={{fontSize:11, fontWeight:'bold', color:'#444', textTransform:'uppercase', letterSpacing:0.8, marginBottom:8}}>Accounts</div>
                      {[
                        ['Chequing', chequingBal, '#1b5e20'],
                        ['Savings', savingsBal, '#1565c0'],
                        ['Credit Card', creditBal, creditBal > 0 ? '#b71c1c' : '#388e3c'],
                        ['Investing', investBal, '#4a148c'],
                      ].filter(([, v]) => v != null).map(([label, val, color]) => (
                        <div key={label} style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'6px 10px', background:'#f8f8f8', borderRadius:6, marginBottom:5}}>
                          <span style={{fontSize:12, color:'#444'}}>{label}</span>
                          <span style={{fontSize:13, fontWeight:'bold', color}}>{label === 'Credit Card' && val > 0 ? `–${fmtCad(val)}` : fmtCad(val)}</span>
                        </div>
                      ))}
                    </div>

                    {/* Net Worth */}
                    {netWorth != null && (
                      <div style={{marginBottom:14, padding:'10px 12px', background: netWorth >= 0 ? '#e8f5e9' : '#ffebee', borderRadius:8, display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                        <span style={{fontSize:12, fontWeight:'bold', color:'#333'}}>Net Worth</span>
                        <span style={{fontSize:15, fontWeight:'bold', color: netWorth >= 0 ? '#1b5e20' : '#b71c1c'}}>{netWorth < 0 ? `–${fmtCad(Math.abs(netWorth))}` : fmtCad(netWorth)}</span>
                      </div>
                    )}

                    {/* Monthly Cash Flow */}
                    {(avgIncome != null || avgExpenses != null) && (
                      <div style={{marginBottom:14}}>
                        <div style={{fontSize:11, fontWeight:'bold', color:'#444', textTransform:'uppercase', letterSpacing:0.8, marginBottom:8}}>Avg Monthly Cash Flow</div>
                        {avgIncome != null && (
                          <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'6px 10px', background:'#f8f8f8', borderRadius:6, marginBottom:5}}>
                            <span style={{fontSize:12, color:'#444'}}>Income</span>
                            <span style={{fontSize:13, fontWeight:'bold', color:'#1b5e20'}}>+{fmtCad(avgIncome)}</span>
                          </div>
                        )}
                        {avgExpenses != null && (
                          <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'6px 10px', background:'#f8f8f8', borderRadius:6, marginBottom:5}}>
                            <span style={{fontSize:12, color:'#444'}}>Expenses</span>
                            <span style={{fontSize:13, fontWeight:'bold', color:'#b71c1c'}}>–{fmtCad(avgExpenses)}</span>
                          </div>
                        )}
                        {avgIncome != null && avgExpenses != null && (() => {
                          const surplus = avgIncome - avgExpenses;
                          return (
                            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'6px 10px', background: surplus >= 0 ? '#e8f5e9' : '#ffebee', borderRadius:6}}>
                              <span style={{fontSize:12, fontWeight:'bold', color:'#333'}}>{surplus >= 0 ? 'Surplus' : 'Deficit'}</span>
                              <span style={{fontSize:13, fontWeight:'bold', color: surplus >= 0 ? '#1b5e20' : '#b71c1c'}}>{surplus >= 0 ? '+' : '–'}{fmtCad(Math.abs(surplus))}</span>
                            </div>
                          );
                        })()}
                      </div>
                    )}

                    {/* Goals */}
                    {goals.length > 0 && (
                      <div>
                        <div style={{fontSize:11, fontWeight:'bold', color:'#444', textTransform:'uppercase', letterSpacing:0.8, marginBottom:8}}>Financial Goals</div>
                        {goals.slice(0, 4).map((g, i) => {
                          const title = g.title || g.name || g.goal_type || `Goal ${i+1}`;
                          const pct = g.current != null && g.target ? Math.min(100, Math.round((g.current / g.target) * 100)) : null;
                          return (
                            <div key={i} style={{marginBottom:8}}>
                              <div style={{display:'flex', justifyContent:'space-between', fontSize:12, color:'#333', marginBottom:3}}>
                                <span>{title}</span>
                                {pct != null && <span style={{color:'#666', fontSize:11}}>{pct}%</span>}
                              </div>
                              {pct != null && (
                                <div style={{height:5, background:'#eee', borderRadius:3, overflow:'hidden'}}>
                                  <div style={{height:'100%', width:`${pct}%`, background: g.color || '#008a3e', borderRadius:3}} />
                                </div>
                              )}
                              {g.target_date && <div style={{fontSize:10, color:'#aaa', marginTop:2}}>Target: {g.target_date}</div>}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })()}
      </div>
      {/* ── CONSOLE PANEL — desktop only ───────────────────────────────────────
          Developer/prompt-engineering workspace. Two tabs:
            LOG    — real-time stream of voice/LLM/API/system events
            PROMPT — live-editable system prompt sections sent to the LLM
          Draggable by title bar, resizable from left edge, traffic-light chrome. */}
      {!isMobile && voiceLogVisible && (() => {
        const savePromptsToMd = () => {
          const now = new Date().toLocaleString('en-CA', { year:'numeric', month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' });
          const section = (key, content) =>
            `<!-- BEGIN:${key} -->\n${content.trim()}\n<!-- END:${key} -->`;
          const md = [
            `# IVA Prompt Configuration`,
            `_Saved: ${now}_`,
            ``,
            section('identity',      promptSystemPrompt),
            ``,
            section('scope',         promptScope),
            ``,
            section('generalRules',  promptGuidelines),
            ``,
            section('safety',        promptSafety),
            ``,
            section('outputFormat',  promptOutputFormat),
            ``,
            section('escalation',    promptEscalation),
            ``,
            section('followUp',      promptFollowUp),
            ``,
            section('summarize',     summarizePrompt),
          ].join('\n');
          const blob = new Blob([md], { type: 'text/markdown' });
          const url  = URL.createObjectURL(blob);
          const a    = document.createElement('a');
          a.href = url; a.download = `iva-prompts-${Date.now()}.md`; a.click();
          URL.revokeObjectURL(url);
        };

        const importPromptsFromMd = (file) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            // Normalise all line endings and strip stray carriage returns.
            const text = e.target.result.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

            // NEW FORMAT: <!-- BEGIN:key --> ... <!-- END:key -->
            const extractNew = (key) => {
              const beginRe = new RegExp(`<!--\\s*BEGIN\\s*:\\s*${key}\\s*-->`, 'i');
              const endRe   = new RegExp(`<!--\\s*END\\s*:\\s*${key}\\s*-->`, 'i');
              const bm = beginRe.exec(text);
              const em = endRe.exec(text);
              if (!bm || !em || em.index <= bm.index) return null;
              return text.slice(bm.index + bm[0].length, em.index).trim() || null;
            };

            // OLD FORMAT: ## Heading _(modified)_\n```\ncontent\n```
            // Heading variants → key mapping
            const OLD_HEADINGS = {
              identity:     ['identity'],
              scope:        ['scope'],
              generalRules: ['general rules', 'general.rules'],
              safety:       ['safety'],
              outputFormat: ['output format', 'output.format', 'visualizations'],
              escalation:   ['escalation'],
              followUp:     ['follow-up qs', 'follow.up qs', 'follow up qs', 'followup'],
              summarize:    ['voice tldr', 'summarize', 'tldr'],
            };
            const extractOld = (key) => {
              const variants = OLD_HEADINGS[key] || [key];
              const pattern = variants.map(v => v.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
              const headingRe = new RegExp(`^##\\s+(?:${pattern})[^\n]*$`, 'im');
              const hm = headingRe.exec(text);
              if (!hm) return null;
              const after = text.slice(hm.index + hm[0].length);
              const fenceOpen = after.indexOf('```');
              if (fenceOpen === -1) return null;
              const contentStart = after.indexOf('\n', fenceOpen) + 1;
              const fenceClose = after.indexOf('```', fenceOpen + 3);
              if (fenceClose === -1 || contentStart <= 0) return null;
              return after.slice(contentStart, fenceClose).trim() || null;
            };

            const extract = (key) => extractNew(key) ?? extractOld(key);

            const identity     = extract('identity');
            const scope        = extract('scope');
            const generalRules = extract('generalRules');
            const safety       = extract('safety');
            const outputFormat = extract('outputFormat');
            const escalation   = extract('escalation');
            const followUp     = extract('followUp');
            const summarize    = extract('summarize');
            pushUndo({ identity: promptSystemPrompt, scope: promptScope, generalRules: promptGuidelines, safety: promptSafety, outputFormat: promptOutputFormat, escalation: promptEscalation, followUp: promptFollowUp, summarize: summarizePrompt, wasSaved: promptsSaved });
            if (identity)     setPromptSystemPrompt(identity);
            if (scope)        setPromptScope(scope);
            if (generalRules) setPromptGuidelines(generalRules);
            if (safety)       setPromptSafety(safety);
            if (outputFormat) setPromptOutputFormat(outputFormat);
            if (escalation)   setPromptEscalation(escalation);
            if (followUp)     setPromptFollowUp(followUp);
            if (summarize)    setSummarizePrompt(summarize);
            setVoiceLogTab('prompt');
          };
          reader.readAsText(file);
        };
        const iconBtn = (onClick, title, children) => (
          <button onClick={onClick} title={title} style={{background:'none', border:'none', color:'#444', cursor:'pointer', padding:'2px 3px', display:'flex', alignItems:'center', borderRadius:3, transition:'color 0.15s'}}
            onMouseEnter={e => e.currentTarget.style.color='#aaa'} onMouseLeave={e => e.currentTarget.style.color='#444'}>
            {children}
          </button>
        );
        return (
          <div style={{position:'fixed', top:130, right:10, width:voiceLogWidth, height: voiceLogMinimized ? 'auto' : 'min(calc(100vh - 150px), 640px)', background:'#0d0d0d', border:'1px solid #2a2a2a', borderRadius:8, display:'flex', flexDirection:'column', fontFamily:'monospace', fontSize:11, zIndex:999, boxShadow:'0 4px 24px rgba(0,0,0,0.5)', overflow:'hidden'}}>

            {/* Left-edge resize handle — drag to change width; right edge stays anchored */}
            <div
              style={{position:'absolute', left:0, top:0, bottom:0, width:6, cursor:'ew-resize', zIndex:10}}
              onMouseDown={e => {
                e.preventDefault();
                const startX = e.clientX;
                const startW = voiceLogWidth;
                const onMove = ev => setVoiceLogWidth(Math.max(280, Math.min(900, startW - (ev.clientX - startX))));
                const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
                document.addEventListener('mousemove', onMove);
                document.addEventListener('mouseup', onUp);
              }}
            />

            {/* ── CHROME: traffic lights + title + icon buttons ── */}
            <div style={{background:'#1a1a1a', borderBottom: voiceLogMinimized ? 'none' : '1px solid #2a2a2a', borderRadius: voiceLogMinimized ? 8 : '8px 8px 0 0', flexShrink:0, userSelect:'none'}}>
              {/* Title row */}
              <div style={{padding:'8px 10px 8px 12px', display:'flex', alignItems:'center', justifyContent:'space-between'}}>
                {/* Traffic lights */}
                <div style={{display:'flex', gap:6, alignItems:'center', flexShrink:0}}>
                  <button onClick={() => setVoiceLogVisible(false)} title="Close console" style={{width:11, height:11, borderRadius:'50%', background:'#ff5f57', border:'none', cursor:'pointer', padding:0, flexShrink:0}} />
                  <button onClick={() => setVoiceLogMinimized(true)}  title="Minimize console" style={{width:11, height:11, borderRadius:'50%', background:'#febc2e', border:'none', cursor:'pointer', padding:0, flexShrink:0}} />
                  <button onClick={() => setVoiceLogMinimized(false)} title="Expand console"  style={{width:11, height:11, borderRadius:'50%', background:'#28c840', border:'none', cursor:'pointer', padding:0, flexShrink:0}} />
                </div>

                {/* Title */}
                <span style={{color:'#888', fontSize:10, letterSpacing:1.5, pointerEvents:'none', flexShrink:0}}>
                  CONSOLE · {voiceLogTab === 'log' ? 'LOG' : voiceLogTab === 'prompt' ? 'PROMPT' : voiceLogTab === 'context' ? 'CONTEXT' : voiceLogTab === 'scenario' ? (simDate ? `SIM ${simDate}` : 'SCENARIO') : 'MEMORY'}
                </span>

                {/* Right icon cluster */}
                <div style={{display:'flex', gap:2, alignItems:'center', flexShrink:0}}>
                  {voiceLogTab === 'log' && iconBtn(() => setVoiceLogs([]), 'Clear log', <MdDeleteSweep size={14} />)}
                  {voiceLogTab === 'memory' && iconBtn(refreshMemory, 'Refresh memory', <MdRefresh size={14} />)}
                  {iconBtn(savePromptsToMd, 'Export prompts to .md file', <MdSaveAlt size={14} />)}
                  <label title="Import prompts from .md file" style={{background:'none', border:'none', color:'#444', cursor:'pointer', padding:'2px 3px', display:'flex', alignItems:'center', borderRadius:3, transition:'color 0.15s'}}
                    onMouseEnter={e => e.currentTarget.style.color='#aaa'} onMouseLeave={e => e.currentTarget.style.color='#444'}>
                    <MdUpload size={14} />
                    <input type="file" accept=".md" style={{display:'none'}} onChange={e => { if (e.target.files[0]) { importPromptsFromMd(e.target.files[0]); e.target.value=''; } }} />
                  </label>
                  {voiceLogTab === 'prompt' && (
                    <button onClick={handleSavePrompts} title="Save prompts to localStorage (survives page reload)"
                      style={{background:'none', border:'none', color: promptsSaved ? '#3a6a3a' : '#febc2e', cursor:'pointer', padding:'2px 3px', display:'flex', alignItems:'center', borderRadius:3, transition:'color 0.15s'}}
                      onMouseEnter={e => e.currentTarget.style.opacity='0.7'} onMouseLeave={e => e.currentTarget.style.opacity='1'}>
                      <MdSave size={14} />
                    </button>
                  )}
                  {voiceLogTab === 'prompt' && (
                    <button onClick={handleResetPrompts} title="Reset all sections to hardcoded defaults and clear saved memory"
                      style={{background:'none', border:'none', color:'#444', cursor:'pointer', padding:'2px 3px', display:'flex', alignItems:'center', borderRadius:3, transition:'color 0.15s'}}
                      onMouseEnter={e => e.currentTarget.style.color='#888'} onMouseLeave={e => e.currentTarget.style.color='#444'}>
                      <MdRestartAlt size={14} />
                    </button>
                  )}
                  <button
                    onClick={handleUndo}
                    disabled={undoDepth === 0}
                    title={undoDepth > 0 ? `Undo last action (${undoDepth} available)` : 'Nothing to undo'}
                    style={{background: undoDepth > 0 ? 'rgba(255,149,0,0.15)' : 'none', border: `1px solid ${undoDepth > 0 ? 'rgba(255,149,0,0.4)' : 'rgba(255,255,255,0.07)'}`, color: undoDepth > 0 ? '#ff9500' : '#2a2a2a', cursor: undoDepth > 0 ? 'pointer' : 'default', padding:'2px 7px', display:'flex', alignItems:'center', gap:3, borderRadius:8, fontSize:9, fontFamily:'monospace', letterSpacing:0.4, transition:'all 0.15s'}}
                    onMouseEnter={e => { if (undoDepth > 0) { e.currentTarget.style.background='rgba(255,149,0,0.28)'; e.currentTarget.style.borderColor='rgba(255,149,0,0.7)'; } }}
                    onMouseLeave={e => { if (undoDepth > 0) { e.currentTarget.style.background='rgba(255,149,0,0.15)'; e.currentTarget.style.borderColor='rgba(255,149,0,0.4)'; } }}>
                    <MdUndo size={12} />{undoDepth > 1 ? `undo ${undoDepth}` : 'undo'}
                  </button>
                </div>
              </div>

              {/* Tab bar — hidden when minimized */}
              {!voiceLogMinimized && (
                <div style={{padding:'0 10px 7px', display:'flex', gap:4, alignItems:'center'}}>
                  {[['log','LOG'],['prompt','PROMPT'],['context','CONTEXT'],['memory','MEMORY'],['scenario', simDate ? '▶ SIM' : 'SCENARIO']].map(([id, label]) => {
                    const isScenario = id === 'scenario';
                    const activeColor = isScenario && simDate ? '#2FBF00' : '#4a9eff';
                    return (
                    <button key={id} onClick={() => setVoiceLogTab(id)}
                      style={{fontSize:10, padding:'2px 10px', borderRadius:10, border:`1px solid ${voiceLogTab === id ? activeColor : '#4a4a4a'}`, background: voiceLogTab === id ? `${activeColor}38` : '#2b2b2b', color: voiceLogTab === id ? activeColor : isScenario && simDate ? '#2FBF00' : '#cfcfcf', cursor:'pointer', letterSpacing:0.8, fontFamily:'monospace', fontWeight: voiceLogTab === id ? 600 : 400, transition:'color 0.15s, background 0.15s'}}
                      onMouseEnter={e => { if (voiceLogTab !== id) { e.currentTarget.style.background='#383838'; e.currentTarget.style.color='#fff'; } }}
                      onMouseLeave={e => { if (voiceLogTab !== id) { e.currentTarget.style.background='#2b2b2b'; e.currentTarget.style.color= isScenario && simDate ? '#2FBF00' : '#cfcfcf'; } }}>
                      {label}
                    </button>
                    );
                  })}
                  {voiceLogTab === 'prompt' && (
                    <span style={{marginLeft:'auto', fontSize:8, color:'#666'}}>
                      {composedSystemPrompt ? `${composedSystemPrompt.length} chars · next msg` : 'using server default'}
                    </span>
                  )}
                </div>
              )}

              {/* Log filter legend — own row beneath the tabs, LOG tab only */}
              {!voiceLogMinimized && voiceLogTab === 'log' && (
                <div style={{padding:'0 10px 7px', display:'flex', gap:4, alignItems:'center', flexWrap:'wrap'}}>
                  <span style={{fontSize:8, color:'#666', letterSpacing:1, fontFamily:'monospace', flexShrink:0}}>filters</span>
                  {Object.keys(LOG_FILTER_GROUPS).map(group => {
                    const active = voiceLogFilters.has(group);
                    const colors = { Voice:'#28c840', LLM:'#febc2e', API:'#5ac8fa', System:'#4a9eff', Errors:'#ff5f57' };
                    return (
                      <button key={group} title={`Toggle ${group} events`}
                        onClick={() => setVoiceLogFilters(prev => { const s = new Set(prev); s.has(group) ? s.delete(group) : s.add(group); return s; })}
                        style={{fontSize:9, padding:'2px 7px', borderRadius:10, border:`1px solid ${active ? colors[group] : '#3a3a3a'}`, background: active ? `${colors[group]}22` : 'transparent', color: active ? colors[group] : '#888', cursor:'pointer', letterSpacing:0.5, fontFamily:'monospace'}}>
                        {group}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* ── LOG TAB ── */}
            {!voiceLogMinimized && voiceLogTab === 'log' && (
              <div style={{flex:1, overflowY:'auto', padding:'8px 10px', display:'flex', flexDirection:'column', gap:3, textAlign:'left', minHeight:0}}>
                {voiceLogs.length === 0 && (
                  <div style={{color:'#333', marginTop:8}}>$ waiting for activity...</div>
                )}
                {voiceLogs.filter(entry => Object.entries(LOG_FILTER_GROUPS).some(([group, types]) => voiceLogFilters.has(group) && types.includes(entry.type))).map(entry => {
                  const colors = { system:'#555', status:'#4a9eff', user:'#28c840', bot:'#febc2e', error:'#ff5f57', tts:'#bf5af2', api:'#5ac8fa' };
                  const prefixes = { system:'SYS', status:'STS', user:'MIC', bot:'BOT', error:'ERR', tts:'TTS', api:'API' };
                  const col = colors[entry.type] || '#555';
                  const pfx = prefixes[entry.type] || '---';
                  return (
                    <div key={entry.id} style={{lineHeight:1.6}}>
                      <span style={{color:'#333'}}>{entry.time} </span>
                      <span style={{color:col, fontWeight:'bold'}}>[{pfx}]</span>
                      <span style={{color: entry.type === 'user' ? '#e0e0e0' : entry.type === 'bot' ? '#ccc' : col === '#555' ? '#444' : '#bbb'}}> {entry.message}</span>
                    </div>
                  );
                })}
                <div ref={voiceLogEndRef} />
              </div>
            )}
            {!voiceLogMinimized && voiceLogTab === 'log' && (
              <div style={{padding:'5px 12px', background:'#1a1a1a', borderTop:'1px solid #2a2a2a', borderRadius:'0 0 8px 8px', color:'#333', fontSize:10, letterSpacing:0.5, flexShrink:0}}>
                {voiceLogs.length} entries · {selectedBackend.toUpperCase()} backend
              </div>
            )}

            {/* ── PROMPT TAB ── */}
            {!voiceLogMinimized && voiceLogTab === 'prompt' && (
              <div style={{flex:1, overflowY:'auto', padding:'10px 12px', display:'flex', flexDirection:'column', gap:14, minHeight:0, fontFamily:'monospace'}}>
                {/* Status banner — full width, no buttons (those are in the title bar icon cluster) */}
                <div style={{padding:'5px 8px', background: promptsSaved ? '#0e1a0e' : '#1a1500', border:`1px solid ${promptsSaved ? '#1e3a1e' : '#3a3000'}`, borderRadius:4}}>
                  <span style={{fontSize:8, color: promptsSaved ? '#3a6a3a' : '#6a5a00', letterSpacing:0.5}}>
                    {promptsSaved ? '● saved to memory — survives reload' : '○ unsaved — changes apply to next message, reset on reload'}
                  </span>
                </div>

                {/* Prompt size indicator */}
                {(() => {
                  const chars  = composedSystemPrompt ? composedSystemPrompt.length : 0;
                  const tokens = Math.round(chars / 4);
                  const color  = tokens < 800 ? '#3a6a3a' : tokens < 1600 ? '#febc2e' : '#ff5f57';
                  const label  = tokens < 800 ? 'lean' : tokens < 1600 ? 'moderate' : 'heavy';
                  const pct    = Math.min(100, Math.round((tokens / 2000) * 100));
                  return (
                    <div style={{display:'flex', flexDirection:'column', gap:4}}>
                      <div style={{display:'flex', justifyContent:'space-between', alignItems:'baseline'}}>
                        <span style={{fontSize:9, color, letterSpacing:0.5, fontWeight:600}}>~{tokens.toLocaleString()} tokens</span>
                        <span style={{fontSize:8, color:'#333', letterSpacing:0.3}}>{chars.toLocaleString()} chars · {label}</span>
                      </div>
                      <div style={{height:2, background:'#1a1a1a', borderRadius:1}}>
                        <div style={{height:'100%', width:`${pct}%`, background:color, borderRadius:1, transition:'width 0.2s'}} />
                      </div>
                    </div>
                  );
                })()}

                {[
                  { label:'Identity',      header:'<identity>',      value:promptSystemPrompt,  setter:setPromptSystemPrompt,  defKey:'identity',     rows:4, hint:'Who the assistant is and what data it can access.' },
                  { label:'Scope',         header:'<scope>',         value:promptScope,         setter:setPromptScope,         defKey:'scope',        rows:4, hint:'What the assistant can and cannot do.' },
                  { label:'Rules',         header:'<rules>',         value:promptGuidelines,    setter:setPromptGuidelines,    defKey:'generalRules', rows:7, hint:'Tone, language style, length, and behaviour rules.' },
                  { label:'Safety',        header:'<safety>',        value:promptSafety,        setter:setPromptSafety,        defKey:'safety',       rows:3, hint:'Data integrity, prompt injection resistance, and role boundaries.' },
                  { label:'Output Format', header:'<output_format>', value:promptOutputFormat,  setter:setPromptOutputFormat,  defKey:'outputFormat', rows:9, hint:'Chart/table rendering rules — pie, bar, line, map, etc.' },
                  { label:'Escalation',    header:'<escalation>',    value:promptEscalation,    setter:setPromptEscalation,    defKey:'escalation',   rows:3, hint:'When to route the customer to a TD advisor or branch.' },
                  { label:'Follow-Up Qs', header:'<follow_up>',     value:promptFollowUp,      setter:setPromptFollowUp,      defKey:'followUp',     rows:3, hint:'How the assistant ends responses with follow-up questions.' },
                ].map(({ label, header, value, setter, defKey, rows, hint }) => {
                  const defVal = DEFAULT_PROMPTS[defKey] || '';
                  const isModified = value.trim() !== defVal.trim();
                  return (
                    <div key={label} style={{display:'flex', flexDirection:'column', gap:4}}>
                      {/* Section label row */}
                      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                        <div style={{display:'flex', alignItems:'center', gap:6}}>
                          <span style={{fontSize:9, letterSpacing:1, color: isModified ? '#febc2e' : '#4a9eff', textTransform:'uppercase', fontWeight:600}}>{label}</span>
                          {header && <span style={{fontSize:8, color:'#2a2a2a', letterSpacing:0.5}}>{header}</span>}
                          {isModified && <span style={{fontSize:8, color:'#febc2e', letterSpacing:0.3}}>●</span>}
                        </div>
                        <div style={{display:'flex', gap:6, alignItems:'center'}}>
                          <span style={{fontSize:8, color:'#2a2a2a'}}>{value.length}c</span>
                          {isModified && (
                            <button onClick={() => setter(defVal)} title={`Reset ${label} to default`}
                              style={{background:'none', border:'none', color:'#555', cursor:'pointer', padding:0, display:'flex', alignItems:'center', gap:2, fontSize:8}}
                              onMouseEnter={e => e.currentTarget.style.color='#888'} onMouseLeave={e => e.currentTarget.style.color='#555'}>
                              <MdRestartAlt size={11} />reset
                            </button>
                          )}
                        </div>
                      </div>
                      {/* Live-editable textarea */}
                      <textarea
                        value={value}
                        onChange={e => setter(e.target.value)}
                        rows={rows}
                        placeholder={hint}
                        spellCheck={false}
                        style={{background:'#111', border:`1px solid ${isModified ? '#3a3020' : '#2a2a2a'}`, borderRadius:4, color:'#ccc', fontSize:10, fontFamily:'monospace', padding:'6px 8px', resize:'vertical', lineHeight:1.5, outline:'none', width:'100%', boxSizing:'border-box'}}
                        onFocus={e => { e.target.style.borderColor = isModified ? '#febc2e' : '#4a9eff'; }}
                        onBlur={e => { e.target.style.borderColor = isModified ? '#3a3020' : '#2a2a2a'; }}
                      />
                      {/* Collapsible original default */}
                      {defVal && (
                        <details style={{marginTop:1}}>
                          <summary style={{fontSize:8, color:'#2a2a2a', cursor:'pointer', letterSpacing:0.4, userSelect:'none', listStyleType:'none', display:'flex', alignItems:'center', gap:3}}>
                            ▶ original default
                          </summary>
                          <div style={{marginTop:3, padding:'5px 8px', background:'#0a0a0a', border:'1px solid #1a1a1a', borderRadius:4, fontSize:9, color:'#3a3a3a', whiteSpace:'pre-wrap', lineHeight:1.5, maxHeight:90, overflowY:'auto'}}>
                            {defVal}
                          </div>
                        </details>
                      )}
                    </div>
                  );
                })}

                {/* Summarize prompt — instruction sent to /api/summarize for voice TTS */}
                <div style={{display:'flex', flexDirection:'column', gap:4}}>
                  <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                    <div style={{display:'flex', alignItems:'center', gap:6}}>
                      <span style={{fontSize:9, letterSpacing:1, color: summarizePrompt.trim() !== DEFAULT_SUMMARIZE_PROMPT.trim() ? '#febc2e' : '#bf5af2', textTransform:'uppercase', fontWeight:600}}>Voice TLDR</span>
                      <span style={{fontSize:8, color:'#2a2a2a', letterSpacing:0.5}}>SUMMARIZE:</span>
                      {summarizePrompt.trim() !== DEFAULT_SUMMARIZE_PROMPT.trim() && <span style={{fontSize:8, color:'#febc2e', letterSpacing:0.3}}>●</span>}
                    </div>
                    <div style={{display:'flex', gap:6, alignItems:'center'}}>
                      <span style={{fontSize:8, color:'#2a2a2a'}}>{summarizePrompt.length}c</span>
                      {summarizePrompt.trim() !== DEFAULT_SUMMARIZE_PROMPT.trim() && (
                        <button onClick={() => setSummarizePrompt(DEFAULT_SUMMARIZE_PROMPT)} title="Reset Voice TLDR to default"
                          style={{background:'none', border:'none', color:'#555', cursor:'pointer', padding:0, display:'flex', alignItems:'center', gap:2, fontSize:8}}
                          onMouseEnter={e => e.currentTarget.style.color='#888'} onMouseLeave={e => e.currentTarget.style.color='#555'}>
                          <MdRestartAlt size={11} />reset
                        </button>
                      )}
                    </div>
                  </div>
                  <textarea
                    value={summarizePrompt}
                    onChange={e => setSummarizePrompt(e.target.value)}
                    rows={3}
                    placeholder="Instruction sent to the LLM before the message text for voice TTS summarization."
                    spellCheck={false}
                    style={{background:'#111', border:`1px solid ${summarizePrompt.trim() !== DEFAULT_SUMMARIZE_PROMPT.trim() ? '#3a3020' : '#2a2a2a'}`, borderRadius:4, color:'#ccc', fontSize:10, fontFamily:'monospace', padding:'6px 8px', resize:'vertical', lineHeight:1.5, outline:'none', width:'100%', boxSizing:'border-box'}}
                    onFocus={e => { e.target.style.borderColor = summarizePrompt.trim() !== DEFAULT_SUMMARIZE_PROMPT.trim() ? '#febc2e' : '#bf5af2'; }}
                    onBlur={e => { e.target.style.borderColor = summarizePrompt.trim() !== DEFAULT_SUMMARIZE_PROMPT.trim() ? '#3a3020' : '#2a2a2a'; }}
                  />
                  <details style={{marginTop:1}}>
                    <summary style={{fontSize:8, color:'#2a2a2a', cursor:'pointer', letterSpacing:0.4, userSelect:'none', listStyleType:'none', display:'flex', alignItems:'center', gap:3}}>
                      ▶ original default
                    </summary>
                    <div style={{marginTop:3, padding:'5px 8px', background:'#0a0a0a', border:'1px solid #1a1a1a', borderRadius:4, fontSize:9, color:'#3a3a3a', whiteSpace:'pre-wrap', lineHeight:1.5, maxHeight:90, overflowY:'auto'}}>
                      {DEFAULT_SUMMARIZE_PROMPT}
                    </div>
                  </details>
                </div>

                {/* Composed prompt preview — what actually gets sent to the LLM */}
                <details style={{marginTop:4}}>
                  <summary style={{fontSize:8, color:'#2a2a2a', cursor:'pointer', letterSpacing:0.5, userSelect:'none', listStyleType:'none', display:'flex', alignItems:'center', gap:4}}>
                    ▶ composed prompt sent to LLM ({composedSystemPrompt?.length ?? 0} chars)
                  </summary>
                  <div style={{marginTop:4, padding:'7px 9px', background:'#080808', border:'1px solid #1a1a1a', borderRadius:4, fontSize:9, color:'#3a3a3a', whiteSpace:'pre-wrap', lineHeight:1.5, maxHeight:130, overflowY:'auto'}}>
                    {composedSystemPrompt || '(all sections empty)'}
                  </div>
                </details>
              </div>
            )}

            {/* ── CONTEXT TAB ── */}
            {!voiceLogMinimized && voiceLogTab === 'context' && (() => {
              const sysChars = composedSystemPrompt ? composedSystemPrompt.length : 0;

              // Estimate actual LLM context size (server compresses profile before sending)
              // Mirrors server buildProfileContext / buildContextFromSummary output sizes
              function estimateContextChars(profileData, summary) {
                if (!profileData && !summary) return 0;
                if (summary) {
                  if (summary._raw) return summary._raw.length; // default source — exact
                  // Approximate buildContextFromSummary text output
                  let est = 150; // customer header lines
                  est += Object.keys(summary.accounts || {}).length * 50 + 12;
                  est += Object.keys(summary.topSpending || {}).length * 30 + 30;
                  est += (summary.monthlyFlow?.length || 0) * 35 + 20;
                  est += (summary.recentTxns?.length || 0) * 55 + 50;
                  est += (summary.goals?.length || 0) * 50 + 10;
                  return est;
                }
                if (!profileData) return 0;
                if (profileData.source === 'default') {
                  const allData = { ...(profileData.transactionsData || {}), ...(profileData.accountTransactions || {}) };
                  let est = 50;
                  for (const txns of Object.values(allData)) {
                    const count = Array.isArray(txns) ? txns.length : 0;
                    const recent = Math.min(count, 10);
                    const older = count - recent;
                    est += 30 + recent * 45 + (older > 0 ? 60 : 0);
                  }
                  return est;
                }
                const p = profileData.profile;
                if (!p) return 0;
                let est = 200;
                for (const acct of Object.values(p.accounts || {})) {
                  const count = Array.isArray(acct.transactions) ? acct.transactions.length : 0;
                  const recent = Math.min(count, 10);
                  const older = count - recent;
                  est += 80 + 35 + recent * 45 + (older > 0 ? 60 : 0);
                }
                est += (p.goals?.length || 0) * 60 + 10;
                return est;
              }

              const profileChars  = estimateContextChars(loadedProfileData, sessionSummary);
              const rawProfileChars = loadedProfileData ? JSON.stringify(loadedProfileData).length : 0;
              const histMsgs     = Array.isArray(activeChatHistory) ? activeChatHistory.length : 0;
              const histChars    = Array.isArray(activeChatHistory)
                ? activeChatHistory.reduce((s, m) => s + (m.text ? m.text.length : 0), 0)
                : 0;
              const totalChars   = sysChars + profileChars + histChars;
              const toTok = c => Math.ceil(c / 4);
              const toKB  = c => (c / 1024).toFixed(1);
              const totalTok = toTok(totalChars);
              const totalColor = totalTok < 4000 ? '#28c840' : totalTok < 8000 ? '#febc2e' : '#ff5f57';
              const totalLabel = totalTok < 4000 ? 'lean' : totalTok < 8000 ? 'moderate' : 'heavy';

              const CtxRow = ({ label, chars, extra }) => {
                const tok = toTok(chars);
                const col = tok < 1000 ? '#3a6a3a' : tok < 3000 ? '#febc2e' : '#ff5f57';
                return (
                  <tr>
                    <td style={{color:'#777', fontSize:10, padding:'4px 8px 4px 0', whiteSpace:'nowrap'}}>{label}</td>
                    <td style={{color:col,   fontSize:10, padding:'4px 8px', textAlign:'right', fontVariantNumeric:'tabular-nums'}}>~{tok.toLocaleString()}</td>
                    <td style={{color:'#555',fontSize:9,  padding:'4px 8px', textAlign:'right', fontVariantNumeric:'tabular-nums'}}>{chars.toLocaleString()} c</td>
                    <td style={{color:'#444',fontSize:9,  padding:'4px 8px', textAlign:'right', fontVariantNumeric:'tabular-nums'}}>{toKB(chars)} KB</td>
                    {extra !== undefined && <td style={{color:'#444',fontSize:9, padding:'4px 8px', textAlign:'right'}}>{extra}</td>}
                  </tr>
                );
              };

              return (
                <div style={{flex:1, overflowY:'auto', padding:'12px 14px', display:'flex', flexDirection:'column', gap:12, minHeight:0, fontFamily:'monospace'}}>
                  {/* Total bar */}
                  <div style={{display:'flex', flexDirection:'column', gap:5}}>
                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'baseline'}}>
                      <span style={{fontSize:9, color:totalColor, letterSpacing:0.5, fontWeight:600}}>~{totalTok.toLocaleString()} tokens total</span>
                      <span style={{fontSize:8, color:'#333'}}>{totalLabel} · {toKB(totalChars)} KB</span>
                    </div>
                    <div style={{height:3, background:'#1a1a1a', borderRadius:1}}>
                      <div style={{height:'100%', width:`${Math.min(100, Math.round((totalTok / 16000) * 100))}%`, background:totalColor, borderRadius:1, transition:'width 0.2s'}} />
                    </div>
                    <div style={{fontSize:8, color:'#2a2a2a', letterSpacing:0.3}}>bar = % of 16k context window</div>
                  </div>

                  {/* Breakdown table */}
                  <table style={{borderCollapse:'collapse', width:'100%'}}>
                    <thead>
                      <tr>
                        <th style={{color:'#333', fontSize:8, fontWeight:400, letterSpacing:0.8, textAlign:'left',  padding:'0 8px 6px 0', borderBottom:'1px solid #222'}}>SECTION</th>
                        <th style={{color:'#333', fontSize:8, fontWeight:400, letterSpacing:0.8, textAlign:'right', padding:'0 8px 6px',   borderBottom:'1px solid #222'}}>~TOKENS</th>
                        <th style={{color:'#333', fontSize:8, fontWeight:400, letterSpacing:0.8, textAlign:'right', padding:'0 8px 6px',   borderBottom:'1px solid #222'}}>CHARS</th>
                        <th style={{color:'#333', fontSize:8, fontWeight:400, letterSpacing:0.8, textAlign:'right', padding:'0 8px 6px',   borderBottom:'1px solid #222'}}>SIZE</th>
                        <th style={{color:'#333', fontSize:8, fontWeight:400, letterSpacing:0.8, textAlign:'right', padding:'0 8px 6px',   borderBottom:'1px solid #222'}}>NOTES</th>
                      </tr>
                    </thead>
                    <tbody>
                      <CtxRow label="System prompt" chars={sysChars} extra={composedSystemPrompt ? 'custom' : 'default'} />
                      <CtxRow label="Profile (est.)" chars={profileChars} extra={sessionSummary ? 'summary' : loadedProfileData ? 'compressed' : 'none'} />
                      {loadedProfileData && (
                        <tr>
                          <td style={{color:'#333', fontSize:9, padding:'2px 8px 4px 0', whiteSpace:'nowrap', paddingLeft:8}}>↳ raw JSON</td>
                          <td style={{color:'#333', fontSize:9, padding:'2px 8px 4px', textAlign:'right', fontVariantNumeric:'tabular-nums'}}>~{toTok(rawProfileChars).toLocaleString()}</td>
                          <td style={{color:'#333', fontSize:9, padding:'2px 8px 4px', textAlign:'right', fontVariantNumeric:'tabular-nums'}}>{rawProfileChars.toLocaleString()} c</td>
                          <td style={{color:'#333', fontSize:9, padding:'2px 8px 4px', textAlign:'right', fontVariantNumeric:'tabular-nums'}}>{toKB(rawProfileChars)} KB</td>
                          <td style={{color:'#333', fontSize:9, padding:'2px 8px 4px', textAlign:'right'}}>not sent</td>
                        </tr>
                      )}
                      <CtxRow label="History"       chars={histChars} extra={`${histMsgs} msg`} />
                      <tr style={{borderTop:'1px solid #222'}}>
                        <td style={{color:totalColor, fontSize:10, fontWeight:600, padding:'6px 8px 4px 0'}}>Total</td>
                        <td style={{color:totalColor, fontSize:10, fontWeight:600, padding:'6px 8px 4px', textAlign:'right', fontVariantNumeric:'tabular-nums'}}>~{totalTok.toLocaleString()}</td>
                        <td style={{color:'#555', fontSize:9, padding:'6px 8px 4px', textAlign:'right', fontVariantNumeric:'tabular-nums'}}>{totalChars.toLocaleString()} c</td>
                        <td style={{color:'#444', fontSize:9, padding:'6px 8px 4px', textAlign:'right', fontVariantNumeric:'tabular-nums'}}>{toKB(totalChars)} KB</td>
                        <td style={{color:totalColor, fontSize:9, padding:'6px 8px 4px', textAlign:'right'}}>{totalLabel}</td>
                      </tr>
                    </tbody>
                  </table>

                  <div style={{fontSize:8, color:'#2a2a2a', letterSpacing:0.3, lineHeight:1.6}}>
                    Tokens ≈ chars ÷ 4 · Profile (est.) = server-compressed context sent to LLM · raw JSON shown as reference, not sent · green &lt;1k · amber 1–3k · red &gt;3k per row
                  </div>
                </div>
              );
            })()}

            {/* ── MEMORY TAB — what IVA has learned about this customer ── */}
            {!voiceLogMinimized && voiceLogTab === 'memory' && (
              <div ref={memoryScrollRef} style={{flex:1, overflowY:'auto', padding:'10px 12px', display:'flex', flexDirection:'column', gap:11, minHeight:0, fontFamily:'monospace', textAlign:'left'}}>
                {!sessionSummary?.customerId ? (
                  <div style={{color:'#444', fontSize:11}}>$ load a profile to inspect memory</div>
                ) : memoryLoading && !memoryData ? (
                  <div style={{color:'#444', fontSize:11}}>$ loading memory…</div>
                ) : !memoryData ? (
                  <div style={{color:'#444', fontSize:11}}>$ no memory learned yet for {sessionSummary.customerId}</div>
                ) : (() => {
                  const m = memoryData;
                  const lbl = { fontSize:9, fontWeight:700, letterSpacing:1, color:'#4a9eff', marginBottom:3, textTransform:'uppercase' };
                  const sec = { display:'flex', flexDirection:'column', gap:2 };
                  const item = { fontSize:11, color:'#bbb', lineHeight:1.5 };
                  const dim = { color:'#555' };
                  const has = (a) => Array.isArray(a) && a.length > 0;
                  const badge = (s) => <span style={{ color: (s==='stated'||s==='confirmed') ? '#28c840' : '#febc2e', fontSize:8 }}> [{s}]</span>;
                  const u = m.understanding;
                  const style = m.interactionStyle || {};
                  const styleBits = ['tone','length','detail','humour','encouragement'].filter(k => style[k]).map(k => `${k}: ${style[k]}`).concat(style.notes || []);
                  return (
                    <>
                      <div style={{...item, color:'#777'}}>
                        {m.customerName || m.customerId}<span style={dim}> · {m.customerId}</span>
                        {m.updatedAt ? <span style={dim}> · upd {String(m.updatedAt).slice(0,16).replace('T',' ')}</span> : null}
                      </div>
                      {has(m.aboutMe) && <div style={sec}><div style={lbl}>About</div>{m.aboutMe.map((f,i) => <div key={i} style={item}>· {f.fact}{f.source ? badge(f.source) : null}</div>)}</div>}
                      {has(m.likes) && <div style={sec}><div style={lbl}>Likes</div>{m.likes.map((l,i) => <div key={i} style={item}>· {l.item}{l.note ? <span style={dim}> ({l.note})</span> : null}</div>)}</div>}
                      {has(m.dislikes) && <div style={sec}><div style={lbl}>Dislikes</div>{m.dislikes.map((d,i) => <div key={i} style={item}>· {d.item}{d.note ? <span style={dim}> ({d.note})</span> : null}</div>)}</div>}
                      {has(m.goals) && <div style={sec}><div style={lbl}>Goals</div>{m.goals.map((g,i) => <div key={i} style={item}>· {g.goal}{g.target ? <span style={dim}> (${g.current ?? '?'} / ${g.target})</span> : null}{g.status && g.status !== 'active' ? <span style={dim}> — {g.status}</span> : null}</div>)}</div>}
                      {has(m.concepts) && <div style={sec}><div style={lbl}>Concepts</div>{m.concepts.map((c,i) => <div key={i} style={item}>· <b style={{color:'#ddd'}}>{c.label}</b>: {(c.rules || []).map(r => `${r.exclude ? '−' : ''}${r.match?.merchant || r.match?.category || '?'}`).join(', ') || '—'}{c.note ? <span style={dim}> ({c.note})</span> : null}</div>)}</div>}
                      {has(m.categoryOverrides) && <div style={sec}><div style={lbl}>Category Overrides</div>{m.categoryOverrides.map((o,i) => <div key={i} style={item}>· {o.merchant}: {o.from} → {o.to}</div>)}</div>}
                      {styleBits.length > 0 && <div style={sec}><div style={lbl}>How To Talk To Them</div>{styleBits.map((b,i) => <div key={i} style={item}>· {b}</div>)}</div>}
                      {u && <div style={sec}><div style={lbl}>Understanding · estimates</div>{u.persona?.summary ? <div style={{...item, color:'#888'}}>{u.persona.summary}</div> : null}{has(u.assumptions) ? u.assumptions.map((a,i) => {
                        const isDismissed = a.status === 'dismissed';
                        const isConfirmed = a.status === 'confirmed';
                        const displayText = isDismissed
                          ? 'Not ' + a.text.replace(/^likely\s+/i, '')
                          : a.text;
                        const textColor = isConfirmed ? '#bbb' : isDismissed ? '#666' : '#888';
                        return (
                          <div key={i} style={{...item, color: textColor}}>
                            · {displayText}
                            {isConfirmed && <span style={{color:'#28c840', fontSize:8}}> [confirmed]</span>}
                          </div>
                        );
                      }) : null}</div>}
                      {has(m.interactionLog) && <div style={sec}><div style={lbl}>Recent Activity</div>{m.interactionLog.slice(-8).reverse().map((e,i) => <div key={i} style={{...item, color:'#777'}}>· <span style={dim}>{String(e.date || '').slice(0,10)}</span> [{e.type}] {String(e.summary || '').slice(0,90)}</div>)}</div>}
                      {has(m.bootstrap?.merchantVocabulary) && <div style={{...item, color:'#444', fontSize:9}}>bootstrap vocabulary · {m.bootstrap.merchantVocabulary.length} merchants</div>}
                    </>
                  );
                })()}
              </div>
            )}

            {/* ── SCENARIO TAB ── */}
            {!voiceLogMinimized && voiceLogTab === 'scenario' && (
              <ScenarioConsoleTab />
            )}
          </div>
        );
      })()}

      <input ref={csvFileRef} type="file" accept=".csv,text/csv" style={{ display: 'none' }} onChange={handleCsvFileChange} />

      {/* Version Indicator — desktop only */}
      {!isMobile && (
        <div style={{position: 'fixed', top: 10, left: 10, padding: '3px 8px', fontSize: '10px', borderRadius: '4px', color: 'rgba(255,255,255,0.25)', letterSpacing: '0.3px', pointerEvents: 'none', userSelect: 'none'}}>
          v{version} | {buildDate}
        </div>
      )}
    </div>
    </ProfileContext.Provider>
    </AppContext.Provider>
  );
}


export default App;
